<template>
	<div class="flex items-center justify-between w-full gap-2 mb-4">
		<PageLabel :helpPageName="'Assessments' + tabName.replace(/\s+/g, '') + subTabName.replace(/\s+/g, '')">{{
			tabName + " - " + subTabName
		}}</PageLabel>
		<div class="flex flex-row items-center gap-2">
			<Button id="btnSaveGeneralInformation" @click.prevent="saveGeneralInformation(true)" v-bind="lock.button.value" v-tooltip="lock.tooltip"
				>Save</Button
			>
			<Button id="btnNext" v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Read)" :disabled="!nextPrevEnabled" @click.stop="nextPage()">
				Next
			</Button>
		</div>
	</div>
	<I2FormRow
		v-if="
			!hideVersion && (props.tabName === tabsDefinitions.syntheticAPI.value || props.tabName === 'Biologics' || props.tabName === 'Product Process')
		"
	>
		<I2FormField style="width: 60%" label="Version">
			<Select
				v-if="displayVersionList"
				v-model="selectedVersion"
				:options="versionList"
				optionLabel="versionName"
				optionValue="versionNumber"
				placeholder="Select a version"
				class="w-full md:w-14rem"
				@change="versionChange($event.value)"
			>
			</Select>
			<InputText
				v-if="!displayVersionList"
				v-model="generalInformation.versionName"
				placeholder="Please enter version name"
				:class="{ 'p-invalid': saveAttempted && !generalInformation.versionName, '': generalInformation.versionName != undefined }"
			/>
		</I2FormField>
		<I2FormField class="mt-5">
			<Button v-if="displayVersionList" id="btnAddNewGeneralInformationVersion" class="btn btn-primary" @click="addNewGeneralInformationVersion()"
				>Add New Version</Button
			>
			<Button
				v-if="!displayVersionList"
				id="btnCancelNewGeneralInformationVersion"
				class="btn btn-primary"
				@click="cancelNewGeneralInformationVersion()"
				>Cancel</Button
			>
		</I2FormField>
	</I2FormRow>
	<div v-if="props.tabName === tabsDefinitions.productProcess.value">
		<I2FormRow>
			<I2FormField label="">
				<div class="flex items-center gap-2 h-full">
					<Checkbox
						v-model="applyDefaultLocation"
						:binary="true"
						inputId="applyDefaultLocationCheckbox"
						v-bind="lock.check.value"
						v-tooltip="lock.tooltip"
					/>
					<label for="applyDefaultLocationCheckbox" class="cursor-pointer whitespace-nowrap">Apply Default Location To Global</label>
				</div>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Product Format">
				<TreeSelect
					v-if="canRenderProductFormatTree"
					v-model="selectedProductFormatIdDisplay"
					:options="productFormatTreeData"
					placeholder="Select a Product Format"
					class="w-full md:w-14rem"
					:class="{ 'p-invalid': saveAttempted && !validateProductFormatSelection(), '': selectedProductFormatId !== null }"
					:filter="productFormatTreeData && productFormatTreeData.length > 10"
					selectionMode="single"
					:showClear="false"
					:metaKeySelection="false"
					emptyMessage="No product formats available"
					v-bind="lock.treeSelect.value"
					v-tooltip="lock.toolTipTargetRoot.value"
				/>
				<Select
					v-else
					v-model="selectedProductFormatId"
					:options="productFormats"
					optionLabel="formatName"
					optionValue="id"
					placeholder="Select a Product Format"
					class="w-full md:w-14rem"
					:class="{
						'p-invalid': saveAttempted && !validateProductFormatSelection(),
						'': selectedProductFormatId !== null,
					}"
					@update:modelValue="checksIfCompOp"
					:filter="productFormats && productFormats.length > 10"
					v-bind="lock.select.value"
					v-tooltip="lock.tooltip"
				>
				</Select>
			</I2FormField>
			<I2FormField :label="massPerProductFormatLabel">
				<InputNumber
					v-model="massPerProductFormat"
					placeholder="Enter mass"
					:inputClass="{ 'p-invalid': saveAttempted && massPerProductFormat == null }"
					:min="0"
					:maxFractionDigits="6"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
			<I2FormField label="Unit">
				<Select
					v-model="selectedMassUnit"
					:options="massUnits"
					optionLabel="unitName"
					optionValue="id"
					placeholder="Select unit"
					class="w-full md:w-14rem"
					:class="{ 'p-invalid': saveAttempted && selectedMassUnit == null, '': selectedMassUnit !== null }"
					v-bind="lock.select.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Dose Strength" v-show="false">
				<InputText
					v-model="doseStrength"
					maxlength="200"
					:class="{ 'p-invalid': (saveAttempted && doseStrength == null) || doseStrength == '', '': doseStrength !== null }"
					@change=""
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
		</I2FormRow>
	</div>
	<div v-if="$props.tabName === tabsDefinitions.biologics.value">
		<I2FormRow>
			<I2FormField label="Bulk Drug Substance (Dry Weight) Mass Output of Step">
				<InputText
					v-model="bulkMassOutput"
					@keypress="onlyNumbers($event)"
					:class="{ 'p-invalid': (saveAttempted && bulkMassOutput == null) || bulkMassOutput == '', '': bulkMassOutput !== null }"
					v-bind="lock.input.value"
					v-tooltip="lock.isLocked.value ? lock.tooltip.value : 'Mass of protein in final product'"
				/>
			</I2FormField>
			<I2FormField label="Unit">
				<div>
					<Select
						v-model="selectedUnit"
						:options="unitsWeight"
						optionLabel="unitName"
						option-value="id"
						placeholder="Select unit"
						class="w-full md:w-14rem"
						:class="{ 'p-invalid': saveAttempted && selectedUnit == null, '': selectedUnit !== null }"
						v-bind="lock.select.value"
						v-tooltip="lock.tooltip"
					>
					</Select>
				</div>
			</I2FormField>
			<I2FormField label="Concentration of BDS in Production Bioreactor Output">
				<InputText
					v-model="concentrationOutput"
					:class="{ 'p-invalid': saveAttempted && isUtilitiesWetBulkFeatureEnabled && !concentrationOutput, '': concentrationOutput !== null }"
					@keypress="onlyNumbers($event)"
					v-bind="lock.input.value"
					v-tooltip="lock.isLocked.value ? lock.tooltip.value : 'Final Concentration of BDS'"
				/>
			</I2FormField>
			<I2FormField label="Unit">
				<div>
					<Select
						v-model="selectedConcentrationUnit"
						:options="concentrationUnits"
						optionLabel="unitName"
						option-value="id"
						placeholder="Select unit"
						class="w-full md:w-14rem"
						:class="{
							'p-invalid': saveAttempted && isUtilitiesWetBulkFeatureEnabled && selectedConcentrationUnit == null,
							'': selectedConcentrationUnit !== null,
						}"
						v-bind="lock.select.value"
						v-tooltip="lock.tooltip"
					>
					</Select>
				</div>
			</I2FormField>
			<I2FormField>
				<template #label>
					Calculated BDS Wet Weight (kg)
					<i
						class="pi pi-info-circle text-color-secondary text-sm ml-1"
						v-tooltip="'Calculated from: Bulk Drug Substance (Dry Weight) Mass Output of Step ÷ Concentration of BDS in Production Bioreactor Output'"
					/>
				</template>
				<InputText
					:value="concentrationWetOutput != null ? Number(concentrationWetOutput).toFixed(6) : concentrationWetOutput"
					readonly
					class="w-full md:w-14rem"
					style="border: none !important; box-shadow: none !important; background: transparent !important; padding-left: 0"
				/>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Site Location">
				<Select
					v-model="selectedSite"
					:options="siteLocations"
					optionLabel="siteLocationName"
					option-value="id"
					placeholder="Select Location"
					:class="{ 'p-invalid': saveAttempted && selectedSite == null, '': selectedSite !== null }"
					filter
					v-bind="lock.select.value"
					v-tooltip="lock.tooltip"
				>
				</Select>
			</I2FormField>
		</I2FormRow>
	</div>

	<div class="mt-5" v-if="props.tabName === tabsDefinitions.packaging.value">
		<!-- Package Name field - Only shown when multi-option feature is enabled -->
		<div class="flex items-end gap-4 mb-4" v-if="isMultiOptionFeatureEnabled">
			<I2FormField label="Package Name" style="width: 50%">
				<InputText
					v-model="generalInformationName"
					maxlength="255"
					placeholder="Enter package name"
					:class="{ 'p-invalid': (saveAttempted && !generalInformationName) || nameAlreadyExists }"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
				<small v-if="nameAlreadyExists" class="text-red-500">A packaging option with this name already exists.</small>
			</I2FormField>
			<Button
				label="Delete Packaging"
				severity="danger"
				size="small"
				@click="openDeleteConfirmDialog('packaging')"
				v-if="canDeleteGeneralInformation"
				v-bind="lock.button.value"
				v-tooltip="lock.tooltip"
			/>
		</div>

		<!-- Device Used Dropdown -->
		<div class="mt-6 max-w-[25%] pr-3">
			<I2FormField label="Device Used">
				<Select
					v-model="packDeviceUsed"
					:options="[
						{ label: 'Yes', value: true },
						{ label: 'No', value: false },
					]"
					optionLabel="label"
					optionValue="value"
					placeholder="Select Yes or No"
					class="w-full"
					@change="packDeviceUsedClick"
					v-bind="lock.select.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
		</div>

		<!-- Calculate Results By and Reference Unit / Product Format selection -->
		<I2FormRow class="mt-5">
			<I2FormField label="Calculate Results By">
				<Select
					v-model="packCalculateResultsBy"
					:options="calculateResultsByOptions"
					optionLabel="label"
					optionValue="value"
					placeholder="Select..."
					class="w-full md:w-14rem"
					v-bind="lock.select.value"
					:disabled="calculateResultsByLock.locked || assessmentStore.isLocked"
					v-tooltip="calculateResultsByLock.locked ? calculateResultsByLock.message : lock.tooltip"
				/>
			</I2FormField>
			<!-- Reference Unit field - shown when Reference Unit is selected -->
			<I2FormField label="Unit" v-if="packCalculateResultsBy === 'referenceUnit'">
				<Select
					v-model="packReferenceUnit"
					:options="packReferenceUnits"
					optionLabel="valueTxt"
					placeholder="Select..."
					class="w-full md:w-14rem"
					:class="{ 'p-invalid': saveAttempted && packReferenceUnit == null, '': packReferenceUnit !== null }"
					v-bind="lock.select.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
			<!-- Product Format fields - shown when Product Format is selected -->
			<I2FormField v-if="packCalculateResultsBy === 'productFormat'">
				<template #label>
					Product Format
					<i
						v-if="packProductFormatReadonly || shouldPackagingFieldsBeReadonly"
						class="pi pi-info-circle ml-2"
						style="cursor: help"
						v-tooltip.top="
							packProductFormatReadonly
								? 'This value is inherited from Product Process and cannot be changed'
								: 'Please set Product Format, Mass per Product Format, and Unit in the Product Process tab first'
						"
					></i>
				</template>
				<div v-if="packProductFormatReadonly || shouldPackagingFieldsBeReadonly" class="readonly-field">
					<InputText
						:value="getSelectedProductFormatName()"
						disabled
						:class="{
							'p-invalid': !getSelectedProductFormatName(),
							'': packSelectedProductFormatId !== null,
						}"
						class="w-full md:w-14rem"
						:placeholder="getSelectedProductFormatName() ? 'Inherited from Product Process' : 'Must be set in Product Process'"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</div>
				<div v-else>
					<TreeSelect
						v-if="canRenderPackFormatTree"
						v-model="packSelectedProductFormatIdDisplay"
						:options="productFormatTreeData"
						placeholder="Select a Product Format"
						class="w-full md:w-14rem"
						:class="{
							'p-invalid': saveAttempted && !packSelectedProductFormatId,
							'': packSelectedProductFormatId !== null,
						}"
						:filter="productFormatTreeData && productFormatTreeData.length > 10"
						selectionMode="single"
						:showClear="false"
						:metaKeySelection="false"
						emptyMessage="No product formats available"
						v-bind="lock.select.value"
						v-tooltip="lock.tooltip"
					/>
					<Select
						v-else
						v-model="packSelectedProductFormatId"
						:options="productFormats"
						optionLabel="formatName"
						optionValue="id"
						placeholder="Select a Product Format"
						class="w-full md:w-14rem"
						:class="{
							'p-invalid': saveAttempted && !packSelectedProductFormatId,
							'': packSelectedProductFormatId !== null,
						}"
						@update:modelValue="checksIfCompOpPack"
						:filter="productFormats && productFormats.length > 10"
						v-bind="lock.select.value"
						v-tooltip="lock.tooltip"
					>
					</Select>
				</div>
			</I2FormField>
			<I2FormField :label="packMassPerProductFormatLabel" v-if="packCalculateResultsBy === 'productFormat'">
				<InputNumber
					v-model="packMassPerProductFormat"
					placeholder="Enter mass"
					:inputClass="{ 'p-invalid': saveAttempted && packMassPerProductFormat == null }"
					:min="0"
					:maxFractionDigits="6"
					:disabled="packProductFormatReadonly || shouldPackagingFieldsBeReadonly"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
			<I2FormField label="Unit" v-if="packCalculateResultsBy === 'productFormat'">
				<Select
					v-model="packSelectedMassUnit"
					:options="packMassUnits"
					optionLabel="unitName"
					optionValue="id"
					placeholder="Select unit"
					class="w-full md:w-14rem"
					:class="{ 'p-invalid': saveAttempted && packSelectedMassUnit == null, '': packSelectedMassUnit !== null }"
					:disabled="packProductFormatReadonly || shouldPackagingFieldsBeReadonly || assessmentStore.isLocked"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
			<I2FormField label="Dose Strength" v-show="false">
				<InputText
					v-model="packDoseStrength"
					maxlength="200"
					:class="{
						'p-invalid': saveAttempted && (packDoseStrength == null || packDoseStrength == ''),
						'': packDoseStrength !== null && packDoseStrength !== '',
					}"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
		</I2FormRow>

		<I2FormRow class="rowContainer mt-5">
			<I2FormField label="Number of Reference Units per Primary Pack">
				<InputText
					v-model="packRefUnitPerPPack"
					maxlength="200"
					@keypress="onlyNumbers($event)"
					:class="{
						'p-invalid': saveAttempted && (packRefUnitPerPPack == null || packRefUnitPerPPack == ''),
						'': packRefUnitPerPPack !== null && packRefUnitPerPPack !== '',
					}"
					class="singleItem"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
			<I2FormField label="Number of Primary Packs per Secondary Pack">
				<InputText
					v-model="packPriPacksPerSecPack"
					maxlength="200"
					@keypress="onlyNumbers($event)"
					:class="{
						'p-invalid': saveAttempted && (packPriPacksPerSecPack == null || packPriPacksPerSecPack == ''),
						'': packPriPacksPerSecPack !== null && packPriPacksPerSecPack !== '',
					}"
					class="singleItem"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
			<I2FormField label="Number of Secondary Packs per Tertiary Pack">
				<InputText
					v-model="packSecPacksPerTerPack"
					maxlength="200"
					@keypress="onlyNumbers($event)"
					:class="{
						'p-invalid': saveAttempted && (packSecPacksPerTerPack == null || packSecPacksPerTerPack == ''),
						'': packSecPacksPerTerPack !== null && packSecPacksPerTerPack !== '',
					}"
					class="singleItem"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
			<I2FormField label="Number of Tertiary Packs per Pallet">
				<InputText
					v-model="packTerPacksPerPallet"
					maxlength="200"
					@keypress="onlyNumbers($event)"
					:class="{
						'p-invalid': saveAttempted && (packTerPacksPerPallet == null || packTerPacksPerPallet == ''),
						'': packTerPacksPerPallet !== null && packTerPacksPerPallet !== '',
					}"
					class="singleItem"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</I2FormField>
		</I2FormRow>

		<!-- Device-specific fields (when Device Used = true) -->
		<template v-if="packDeviceUsed">
			<I2FormRow class="rowContainer">
				<I2FormField class="">
					<template #label>
						Lifetime Reference Units per Device
						<button
							@click.prevent="
								EventBus.emit('OpenPageHelp', {
									helpPageName: 'PackagingLifetimeRefUnitsPerDevice',
									userFriendlyName: 'Lifetime Reference Units per Device',
								})
							"
							v-tooltip.top="'View Field Help'"
							class="ml-1"
							style="background: none; border: none; padding: 0; cursor: pointer"
						>
							<i class="pi pi-info-circle text-blue-500"></i>
						</button>
					</template>
					<InputText
						v-model="packLifetimeRefUnitsPerDevice"
						maxlength="200"
						class="singleItem"
						@keypress="onlyNumbers($event)"
						:class="{ 'p-invalid': saveAttempted && !packLifetimeRefUnitsPerDevice }"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</I2FormField>
				<I2FormField label="Number of Devices per Device Secondary pack">
					<InputText
						v-model="packDevicePerDevSecPack"
						maxlength="200"
						@keypress="onlyNumbers($event)"
						:class="{ 'p-invalid': saveAttempted && packDevicePerDevSecPack == null, '': packDevicePerDevSecPack !== null }"
						class="singleItem"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</I2FormField>
				<I2FormField label="Number of Device Secondary Packs per Device Tertiary pack">
					<InputText
						v-model="packDevSecPerDevTer"
						maxlength="200"
						@keypress="onlyNumbers($event)"
						:class="{ 'p-invalid': saveAttempted && packDevSecPerDevTer == null, '': packDevSecPerDevTer !== null }"
						class="singleItem"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</I2FormField>
				<I2FormField label="Number of Device Tertiary Packs per Device pallet">
					<InputText
						v-model="packDevTerPerDevPallet"
						maxlength="200"
						@keypress="onlyNumbers($event)"
						:class="{ 'p-invalid': saveAttempted && packDevTerPerDevPallet == null, '': packDevTerPerDevPallet !== null }"
						class="singleItem"
						v-bind="lock.input.value"
						v-tooltip="lock.tooltip"
					/>
				</I2FormField>
			</I2FormRow>
		</template>

		<!-- Notes field -->
		<I2FormRow>
			<I2FormField label="Notes">
				<Textarea v-model="notes" placeholder="Please enter notes" rows="2" autoResize v-bind="lock.input.value" v-tooltip="lock.tooltip" />
			</I2FormField>
		</I2FormRow>

		<I2FormRow class="">
			<div style="border: 1px black solid; border-radius: 5px" class="py-2 px-4">
				<b>Note</b> - This screen gathers input information which impacts the results. To avoid result mis-match, changes to fields in the general
				information tab after entering data needs extra consideration. If data is changed, delete and re-enter a Packaging Materials or adjust a
				parameter for all materials to trigger auto-save and recalculation of results to reflect the changes within the general information tab.
				Please also note that if you are selecting device and populating information for a device, that this information has to also be repeated in
				the input for packaging in the general information tab to move forward.
			</div>
		</I2FormRow>
	</div>
	<div v-if="props.tabName === tabsDefinitions.distribution.value">
		<!-- Distribution Name field - Only shown when multi-option feature is enabled -->
		<div class="flex items-end gap-4 mb-4 mt-5" v-if="isMultiOptionFeatureEnabled">
			<I2FormField label="Distribution Name" style="width: 50%">
				<InputText
					v-model="generalInformationName"
					maxlength="255"
					placeholder="Enter distribution name"
					:class="{ 'p-invalid': (saveAttempted && !generalInformationName) || nameAlreadyExists }"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
				<small v-if="nameAlreadyExists" class="text-red-500">A distribution option with this name already exists.</small>
			</I2FormField>
			<Button
				label="Delete Distribution"
				severity="danger"
				size="small"
				@click="openDeleteConfirmDialog('distribution')"
				:disabled="!canDeleteGeneralInformation || assessmentStore.isLocked"
				v-tooltip="lock.tooltip"
			/>
		</div>

		<div class="flex items-center justify-between w-full gap-2 mb-4 mt-6">
			<PageLabel :helpPageName="'Assessments' + tabName.replace(/\s+/g, '') + subTabName.replace(/\s+/g, '') + 'ProductParameters'">
				Product Parameters</PageLabel
			>
		</div>

		<i2FormRow class="ratings flex-nowrap ratings-hide">
			<span class="field three-fourth">Total drug product mass per pallet</span>

			<h2 class="field one-fourth">
				<InputText
					v-model="distDrugProductMass"
					maxlength="20"
					@keypress="onlyNumbers($event)"
					class="mx-2"
					:class="{
						'p-invalid':
							props.tabName === tabsDefinitions.distribution.value
								? (saveAttempted && distDrugProductMass == null) || drugMassToKgDistribution() > distTotalMassOfPallet
								: false,
						'': distDrugProductMass !== null,
					}"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</h2>
			<h2 class="field one-fourth">
				<Select
					v-model="distDrugProductMassUnit"
					:options="distReferenceUnits"
					optionLabel="unitName"
					placeholder="Select a Reference Unit"
					class="w-full md:w-14rem"
					:class="{ 'p-invalid': saveAttempted && distDrugProductMassUnit == null, '': distDrugProductMassUnit !== null }"
					v-bind="lock.select.value"
					v-tooltip="lock.tooltip"
				>
				</Select>
			</h2>
			<h2 class="field one-eighths"></h2>
			<h2 class="field one-eighths"></h2>
			<h2 class="field one-eighths"></h2>
			<h2 class="field one-eighths"></h2>
		</i2FormRow>
		<i2FormRow class="ratings flex-nowrap ratings-hide mt-6">
			<span class="field three-fourth">Total mass of pallet</span>

			<h2 class="field one-fourth">
				<InputText
					v-model="distTotalMassOfPallet"
					maxlength="20"
					@keypress="onlyNumbers($event)"
					class="mx-2"
					:class="{
						'p-invalid':
							props.tabName === tabsDefinitions.distribution.value
								? (saveAttempted && distTotalMassOfPallet == null) || drugMassToKgDistribution() > distTotalMassOfPallet
								: false,
						'': distTotalMassOfPallet !== null,
					}"
					v-bind="lock.input.value"
					v-tooltip="lock.tooltip"
				/>
			</h2>
			<h2 class="field one-fourth">kg</h2>
			<h2 class="field one-eighths"></h2>
			<h2 class="field one-eighths"></h2>
			<h2 class="field one-eighths"></h2>
			<h2 class="field one-eighths"></h2>
		</i2FormRow>
	</div>
	<div v-if="generalInformationId === null">complete all field(s) above and click save</div>
	<div
		class="mt-5"
		v-if="props.tabName !== tabsDefinitions.packaging.value && props.tabName !== tabsDefinitions.distribution.value && generalInformationId"
	>
		<commonStep
			:assessmentId="props.assessmentId"
			:screenId="props.tabId"
			:generalInformationId="generalInformationId"
			:tabName="props.tabName"
			:selectedCompOptions="selectedCompOperations"
			:allTabsInformation="props.allTabsInformation"
			:highlightedStepId="props.highlightedStepId"
			:isTemplateCopyEnabled="props.isTemplateCopyEnabled"
			@stepHovered="(stepId: string | null) => emit('stepHovered', stepId)"
		></commonStep>
	</div>

	<Toast position="top-right" :group="props.tabId" />
	<Dialog v-model:visible="showDialogDeviceUsed" :modal="true" style="width: 35vw" :closable="false" contentStyle="overflow:visible">
		<template #header>
			<PageLabel :helpPageName="'materialDelete'">Device Used </PageLabel>
		</template>
		<FormCard>
			<I2FormRow class="mt-6">
				<h3>
					{{ dialogDeviceUsedMessage }}
				</h3>
			</I2FormRow>
		</FormCard>
		<template #footer>
			<I2FormRow right>
				<Button label="Ok" class="inverted mr-4" @click="showDialogDeviceUsed = false" right />
			</I2FormRow>
		</template>
	</Dialog>
	<Dialog v-model:visible="showDialogWrongIds" :modal="true" style="width: 65vw" :closable="false" contentStyle="overflow:visible">
		<template #header>
			<PageLabel>Intermediated Used </PageLabel>
		</template>

		<FormCard>
			<!--<DataTable :value="intermediateChecksResult" responsiveLayout="scroll" :expandedRows.sync="expandedRows">
				<template #groupheader="slotProps">
					<span class="vertical-align-middle ml-2 font-bold line-height-3">{{ slotProps.data.stepName }}</span></template
				>
				<template #expansion="{ data }">
					<div v-if="data.intermediates.length > 0">
						<DataTable :value="data.intermediates" responsiveLayout="scroll"
							><Column field="isWrong" header="Wrong">
								<template #body="{ data }">
									<div class="w-full" v-if="!data.isWrong">{{ data.isWrong ? "Yes" : "No" }}</div>
									<div v-else><Button label="Fix" class="inverted mr-4" /></div> </template></Column
							><Column field="possibleRightIntermediateName" header="Intermediate Name"></Column
						></DataTable>
					</div>
					<p v-else>No intermediates available.</p>
				</template>
				<Column expander></Column>
				<Column field="stepName" header="Step Name"
					><template #body="{ data }">
						<div class="w-full">{{ data.stepName }}</div>
					</template></Column
				>
			</DataTable>
			-->
			<DataTable :value="intermediateChecksResult" responsiveLayout="scroll" :expandedRows.sync="expandedRows" scrollable scroll-height="500px">
				<Column field="stepName" header="Step Name">
					<template #body="{ data }">
						<div class="flex items-center">
							<span>{{ data.stepName }}</span>
						</div>
					</template>
				</Column>
				<Column header="Intermediates" expander>
					<template #body="{ data }">
						<div class="flex items-center">
							<Button icon="pi pi-chevron-down" class="p-button-text p-button-sm mr-2" @click="toggleRow(data)" /></div></template
				></Column>

				<template #expansion="{ data }">
					<div v-if="data.intermediates.length > 0">
						<DataTable :value="data.intermediates" responsiveLayout="scroll"
							><Column field="isWrong" header="Wrong">
								<template #body="{ data }">
									<div class="w-full" v-if="!data.isWrong">{{ data.isWrong ? "Yes" : "No" }}</div>
									<div v-else><Button label="Fix" class="inverted mr-4" /></div> </template></Column
							><Column field="possibleRightIntermediateName" header="Intermediate Name"></Column
						></DataTable>
					</div>
					<p v-else>No intermediates available.</p>
				</template>
			</DataTable>
		</FormCard>
		<template #footer>
			<I2FormRow right>
				<Button label="Ok" class="inverted mr-4" @click="showDialogWrongIds = false" right />
			</I2FormRow>
		</template>
	</Dialog>
	<!-- Delete Confirmation Dialog for Package/Distribution -->
	<Dialog v-model:visible="showDeleteConfirmDialog" :modal="true" style="width: 30vw" :closable="false">
		<template #header>
			<PageLabel>{{ deleteDialogTitle }}</PageLabel>
		</template>
		<div class="p-4">
			<p>Are you sure you want to delete this {{ deleteDialogItemType }}?</p>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2">
				<Button label="Cancel" class="inverted" @click="showDeleteConfirmDialog = false" />
				<Button label="Delete" severity="danger" @click="confirmDeleteGeneralInformation" />
			</div>
		</template>
	</Dialog>
</template>
<script setup lang="ts">
import { ref, onMounted, watchEffect, watch, computed, type PropType } from "vue"
import Textarea from "primevue/textarea"
import Select from "primevue/select"
import TreeSelect from "primevue/treeselect"
import InputText from "primevue/inputtext"
import InputNumber from "primevue/inputnumber"
import Checkbox from "primevue/checkbox"
import Button from "primevue/button"

import Dialog from "primevue/dialog"

import {
	ApiClient,
	ProductFormatClient,
	IntermediateStatusStep,
	GeneralInformation,
	ProductFormat,
	UpdateDevicePackagingRequest,
	FeatureFlagClient,
	GeneralInformationClient,
	WetWeightCalculationRequest,
} from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import commonStep from "../components/commonStep.vue"
import { useToast } from "primevue/usetoast"
import { EventBus } from "@/eventBus"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import { useTabsDefinitions } from "../composables/tabsDefinitions"
import { useHistoryHelper } from "../composables/historyHelper"
import { useProductFormatLabel } from "../composables/useProductFormatLabel"
import { useAssessmentStore } from "@/store/assessmentStore"
import { useAssessmentLock } from "@/composables/assessmentLock"

const assessmentStore = useAssessmentStore()
const lock = useAssessmentLock(assessmentStore)
const tabsDefinitions = useTabsDefinitions()
const { updateAssessmentUpdatedByDate } = useHistoryHelper()

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const productFormatClient = new ProductFormatClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())
const generalInformationClient = new GeneralInformationClient(import.meta.env.VITE_API_URL?.toString())
interface IVersionList {
	versionNumber: number
	versionName: string
}

const props = defineProps({
	assessmentId: {
		type: String,
		required: true,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	subTabName: {
		type: String,
		required: true,
		default: "",
	},
	tabId: {
		type: String,
		required: true,
		default: "",
	},
	isCollapsed: {
		type: Boolean,
		required: false,
		default: "",
	},
	allTabsInformation: {
		type: Object,
		required: true,
		default: "",
	},
	highlightedStepId: {
		type: String as PropType<string | null>,
		required: false,
		default: null,
	},
	isTemplateCopyEnabled: {
		type: Boolean,
		required: false,
		default: false,
	},
})

const notes = ref()
const doseStrength = ref()
const doseForm = ref()
const doseUnit = ref()
const applyDefaultLocation = ref()
const selectedVersion = ref(0)
//const screenId = ref()
const generalInformationId = ref<string | null | undefined>(null)
const versionList = ref([] as IVersionList[])
let newVersionNumber = 0
//const versionName = ref()
let toast = useToast()

const getDone = ref(false)
const displayVersionList = ref(false)
const saveAttempted = ref(false)

const nextPrevEnabled = ref(false)

// Feature flag for utilities calculated using wet bulk substance
const isUtilitiesWetBulkFeatureEnabled = ref(false)

// Feature flag for multi-option on Packaging and Distribution
const isMultiOptionFeatureEnabled = ref(false)

const doseForms = ref()
const doseUnits = ref()

//Biologics
const siteLocations = ref()
const selectedSite = ref()
const bulkMassOutput = ref()
const selectedUnit = ref()
const unitsWeight = ref()
const concentrationUnits = ref()
const concentrationOutput = ref()
const concentrationWetOutput = ref()
const selectedConcentrationUnit = ref()

//Product Process
const compOperations = ref()
const compOpSelected = ref()
const selectedCompOperations = ref()
const unitOperations = ref()
const productFormats = ref([] as ProductFormat[])
const productFormatTreeData = ref([] as any[])
const productFormatCache = ref([] as ProductFormat[]) // Cache for product formats with massQuantityLabelText
const selectedProductFormatId = ref()
const massPerProductFormat = ref()
const selectedMassUnit = ref()
const massUnits = ref([] as any[])
const useTreeSelect = ref(false)

//Packaging
const packMassUnits = ref([] as any[])
const packDeviceUsed = ref(false)

// Calculate Results By options for Packaging
const packCalculateResultsBy = ref(null)
const calculateResultsByOptions = ref([
	{ label: "Reference Unit", value: "referenceUnit" },
	{ label: "Product Format", value: "productFormat" },
])

// Flag to control Packaging Materials tab behavior
// Set to false: Packaging Materials is ENABLED when Device Materials is DISABLED (inverse logic)
// Set to true: Packaging Materials remains always active regardless of Device Materials state
const alwaysEnablePackagingMaterials = ref(true)

const packReferenceUnit = ref()
const packReferenceUnits = ref()
const packSelectedProductFormatId = ref()
const packMassPerProductFormat = ref()
const packSelectedMassUnit = ref()
const packProductFormatReadonly = ref(false) // True if inherited from Product Process
// True when product process has product format, mass, and unit all configured
const calculateResultsByLockedByProductProcess = computed(
	() => packProductFormatReadonly.value && packMassPerProductFormat.value != null && packSelectedMassUnit.value != null
)
// Returns locked state and tooltip message for the Calculate Results By dropdown
const calculateResultsByLock = computed<{ locked: boolean; message: string | null }>(() => {
	if (calculateResultsByLockedByProductProcess.value) {
		return {
			locked: true,
			message: "Calculate Results By is locked to Product Format because Product Process has Product Format, Mass, and Unit configured",
		}
	}
	return { locked: false, message: null }
})
const packDoseStrength = ref()
const packRefUnitPerPPack = ref()
const packPriPacksPerSecPack = ref()
const packSecPacksPerTerPack = ref()
const packTerPacksPerPallet = ref()
const packLifetimeRefUnitsPerDevice = ref()
const packDevicePerDevSecPack = ref()
const packDevSecPerDevTer = ref()
const packDevTerPerDevPallet = ref()

// Packaging/Distribution Name (shared field for both tabs)
const generalInformationName = ref<string>("")
const existingOptionNames = ref<string[]>([])
const nameAlreadyExists = computed(() => {
	if (!generalInformationName.value || !isMultiOptionFeatureEnabled.value) return false
	return existingOptionNames.value.some((name) => name === generalInformationName.value.trim() && name !== originalLoadedName.value)
})
const originalLoadedName = ref<string>("")

//Distribution
const distDrugProductMass = ref()
const distDrugProductMassUnit = ref()
//const distDosesPerPallet = ref()
const distTotalMassOfPallet = ref()
const distTotalMassOfPalletUnit = ref()
const distReferenceUnits = ref()

let screenId = ""
let generalInformation = new GeneralInformation()
let generalInformationList = ref([] as GeneralInformation[])
let hideVersion = true

//dialog device used
const showDialogDeviceUsed = ref()
const dialogDeviceUsedMessage = ref()

// Delete confirmation dialog for Package/Distribution
const showDeleteConfirmDialog = ref(false)
const deleteDialogTitle = ref("")
const deleteDialogItemType = ref("")
const activeGeneralInformationCount = ref(0)

// Computed property to check if delete is allowed (at least 2 active options and a valid selection)
const canDeleteGeneralInformation = computed(() => {
	return generalInformationId.value && activeGeneralInformationCount.value >= 2
})

let oldProductFormatId: string | undefined = ""

// Check if Product Process tab exists in the subproject (using database verification)
const hasProductProcessTab = ref(false)

// Function to check if subproject has Product Process screen via API
async function checkSubprojectHasProductProcess(): Promise<void> {
	if (!props.assessmentId) return

	try {
		const result = await apiClient.checkIfSubprojectHasProductProcessScreen(props.assessmentId)
		hasProductProcessTab.value = result
	} catch (error) {
		console.error("Error checking if subproject has Product Process screen:", error)
		hasProductProcessTab.value = false
	}
}

// Track if Product Process has data to determine field availability
const productProcessHasData = ref(false)

// Check if Product Process has required data
async function checkProductProcessData(): Promise<void> {
	if (!props.assessmentId || !hasProductProcessTab.value) {
		productProcessHasData.value = false
		return
	}

	try {
		// Check if Product Process has productFormatId data
		const genInfo = await apiClient.getProductFormatIdFromProductProcess(props.assessmentId)
		productProcessHasData.value = !!genInfo?.productFormatId
	} catch (error) {
		console.error("Error checking Product Process data:", error)
		productProcessHasData.value = false
	}
}

// Check if packaging fields should be read-only (when Product Process exists but fields are not completed)
const shouldPackagingFieldsBeReadonly = computed(() => {
	// Only apply this logic in packaging tab when Product Process tab exists
	if (props.tabName !== tabsDefinitions.packaging.value || !hasProductProcessTab.value) {
		return false
	}

	// If data is already inherited (readonly), don't block fields
	if (packProductFormatReadonly.value) {
		return false
	}

	// Block fields if Product Process exists but required fields are not filled
	// Allow fields if Product Process has data or if no Product Process tab
	return !productProcessHasData.value
})

// Create separate refs for display (TreeSelect format) and API (clean string)
// IMPORTANT: Initialize with {} to prevent TreeSelect errors on undefined
const selectedProductFormatIdDisplay = ref({})
const packSelectedProductFormatIdDisplay = ref({})

// Computed to check if TreeSelect can safely render (selected value exists in tree)
const canRenderProductFormatTree = computed(() => {
	if (!useTreeSelect.value || productFormatTreeData.value.length === 0) return false

	// If no value selected, it's safe to render
	if (!selectedProductFormatIdDisplay.value || Object.keys(selectedProductFormatIdDisplay.value).length === 0) return true

	// If value selected, verify it exists in tree
	const keys = Object.keys(selectedProductFormatIdDisplay.value)
	if (keys.length > 0) {
		return idExistsInTree(productFormatTreeData.value, keys[0])
	}
	return true
})

const canRenderPackFormatTree = computed(() => {
	if (!useTreeSelect.value || productFormatTreeData.value.length === 0) return false

	// If no value selected, it's safe to render
	if (!packSelectedProductFormatIdDisplay.value || Object.keys(packSelectedProductFormatIdDisplay.value).length === 0) return true

	// If value selected, verify it exists in tree
	const keys = Object.keys(packSelectedProductFormatIdDisplay.value)
	if (keys.length > 0) {
		return idExistsInTree(productFormatTreeData.value, keys[0])
	}
	return true
})

// Watchers to sync between display and clean API values
watch(
	selectedProductFormatIdDisplay,
	(newValue) => {
		if (newValue && typeof newValue === "object" && !Array.isArray(newValue)) {
			const keys = Object.keys(newValue)
			if (keys.length === 1 && newValue[keys[0]] === true) {
				selectedProductFormatId.value = keys[0]
			} else if (keys.length === 0) {
				// TreeSelect cleared - set to null
				selectedProductFormatId.value = null
			}
		} else {
			selectedProductFormatId.value = newValue
		}
	},
	{ deep: true }
)

watch(
	packSelectedProductFormatIdDisplay,
	(newValue) => {
		if (newValue && typeof newValue === "object" && !Array.isArray(newValue)) {
			const keys = Object.keys(newValue)
			if (keys.length === 1 && newValue[keys[0]] === true) {
				packSelectedProductFormatId.value = keys[0]
			}
		} else {
			packSelectedProductFormatId.value = newValue
		}
	},
	{ deep: true }
)

// Helper function to get the selected product format name for Product Process tab
function getProductProcessFormatName(): string {
	if (!selectedProductFormatId.value) return ""

	// Try to find the name in productFormats (flat list)
	const formatFromList = productFormats.value.find((f) => f.id === selectedProductFormatId.value)
	if (formatFromList) return formatFromList.formatName || ""

	// Try to find in tree data (recursively search all nodes)
	function findInTree(nodes: any[], id: string): string | null {
		for (const node of nodes) {
			if (node.key === id) {
				return node.label || node.name || id
			}
			if (node.children && node.children.length > 0) {
				const found = findInTree(node.children, id)
				if (found) return found
			}
		}
		return null
	}

	const nameFromTree = findInTree(productFormatTreeData.value, selectedProductFormatId.value)
	return nameFromTree || selectedProductFormatId.value
}

// Computed property to get the selected product format name for Product Process tab
const selectedProductFormatName = computed(() => {
	return getProductProcessFormatName() || null
})

// Computed property to get the selected product format name for Packaging tab
const packSelectedProductFormatName = computed(() => {
	return getSelectedProductFormatName() || null
})

// Helper function to get massQuantityLabelText from cache
function getMassQuantityLabelText(productFormatId: string | null | undefined): string | null {
	if (!productFormatId) return null
	const format = productFormatCache.value.find((pf) => pf.id === productFormatId)
	return format?.massQuantityLabelText || null
}

// Computed properties for massQuantityLabelText
const selectedProductFormatLabelText = computed(() => {
	return getMassQuantityLabelText(selectedProductFormatId.value)
})

const packSelectedProductFormatLabelText = computed(() => {
	return getMassQuantityLabelText(packSelectedProductFormatId.value)
})

// Use the composable to get dynamic labels for both tabs
const { massPerProductFormatLabel } = useProductFormatLabel(selectedProductFormatName, selectedProductFormatLabelText)
const { massPerProductFormatLabel: packMassPerProductFormatLabel } = useProductFormatLabel(
	packSelectedProductFormatName,
	packSelectedProductFormatLabelText
)

// Helper function to check if an ID exists in the tree
function idExistsInTree(nodes: any[], id: string): boolean {
	for (const node of nodes) {
		if (node.key === id) {
			return true
		}
		if (node.children && node.children.length > 0) {
			if (idExistsInTree(node.children, id)) {
				return true
			}
		}
	}
	return false
}

// Reverse watchers to sync API values back to display when loading data
watch(selectedProductFormatId, (newValue) => {
	if (newValue && typeof newValue === "string") {
		// Convert string ID to TreeSelect object format for display
		if (useTreeSelect.value) {
			// Only set the value if the ID exists in the tree to avoid errors
			if (productFormatTreeData.value.length === 0 || idExistsInTree(productFormatTreeData.value, newValue)) {
				selectedProductFormatIdDisplay.value = { [newValue]: true }
			} else {
				console.warn(`Product format ID ${newValue} not found in tree data`)
				selectedProductFormatIdDisplay.value = {}
			}
		} else {
			selectedProductFormatIdDisplay.value = newValue
		}
	} else if (!newValue) {
		// Clear the display when value is null/undefined
		if (useTreeSelect.value) {
			selectedProductFormatIdDisplay.value = {}
		} else {
			selectedProductFormatIdDisplay.value = null
		}
	}
})

watch(packSelectedProductFormatId, (newValue) => {
	if (newValue && typeof newValue === "string") {
		// Convert string ID to TreeSelect object format for display
		if (useTreeSelect.value) {
			// Only set the value if the ID exists in the tree to avoid errors
			if (productFormatTreeData.value.length === 0 || idExistsInTree(productFormatTreeData.value, newValue)) {
				packSelectedProductFormatIdDisplay.value = { [newValue]: true }
			} else {
				console.warn(`Pack product format ID ${newValue} not found in tree data`)
				packSelectedProductFormatIdDisplay.value = {}
			}
		} else {
			packSelectedProductFormatIdDisplay.value = newValue
		}
	}
})

// Watch packCalculateResultsBy to reset the other field when switching between Reference Unit and Product Format
watch(packCalculateResultsBy, async (newValue, oldValue) => {
	// Only reset if we're switching from one option to another (not on initial load)
	if (oldValue !== null && oldValue !== undefined && newValue !== oldValue) {
		if (newValue === "referenceUnit") {
			// Switching to Reference Unit - reset Product Format fields
			packSelectedProductFormatId.value = null
			packSelectedProductFormatIdDisplay.value = {}
			packMassPerProductFormat.value = null
			packSelectedMassUnit.value = null
		} else if (newValue === "productFormat") {
			// Switching to Product Format - reset Reference Unit field
			packReferenceUnit.value = null
			// Auto-populate Product Format fields from Product Process if available
			await checkForProductProcessInheritance()
		}
	}
})

// Watch hasProductProcessTab to check for Product Process data when it changes
watch(hasProductProcessTab, (newValue) => {
	if (newValue) {
		checkProductProcessData()
	} else {
		productProcessHasData.value = false
	}
})

// Watch for tab changes to run Product Process checks
watch(
	() => props.tabName,
	async (newTabName) => {
		// When entering packaging tab, check for Product Process
		if (newTabName === tabsDefinitions.packaging.value) {
			// Check if subproject has Product Process screen
			await checkSubprojectHasProductProcess()

			// Check if Product Process has data
			await checkProductProcessData()

			// Check for Product Process inheritance
			await checkForProductProcessInheritance()
		}
	},
	{ immediate: true } // Run immediately on component mount
)

// Function to calculate wet weight via API
const calculateWetWeight = async () => {
	// Only calculate if we have both dry weight and concentration
	if (!bulkMassOutput.value || !concentrationOutput.value || concentrationOutput.value <= 0) {
		concentrationWetOutput.value = null
		return
	}

	// Validate that both units are selected before making the API call
	if (!selectedUnit.value || !selectedConcentrationUnit.value) {
		concentrationWetOutput.value = null
		return
	}

	// Ensure we have a valid generalInformation ID before calling the API
	// Check for null, undefined, or empty GUID
	if (!generalInformation.id || generalInformation.id === "00000000-0000-0000-0000-000000000000") {
		generalInformation.id = self.crypto.randomUUID()
		// Set version number for new general information
		generalInformation.versionNumber = 1
		generalInformation.versionName = "Version 1"
	}

	try {
		const request = new WetWeightCalculationRequest({
			generalInformationId: generalInformation.id,
			dryWeight: bulkMassOutput.value,
			dryWeightUnitId: selectedUnit.value,
			concentration: concentrationOutput.value,
			concentrationUnitId: selectedConcentrationUnit.value,
			screenId: props.tabId,
			assessmentId: props.assessmentId,
			versionNumber: generalInformation.versionNumber,
			versionName: generalInformation.versionName,
		})

		const response = await generalInformationClient.calculateWetWeight(request)

		concentrationWetOutput.value = response.wetWeight
	} catch (error) {
		console.error("Error calculating wet weight:", error)
	}
}

// Debounced version of calculateWetWeight to avoid excessive API calls
let wetWeightDebounceTimer: ReturnType<typeof setTimeout> | null = null
const debouncedCalculateWetWeight = () => {
	if (wetWeightDebounceTimer) {
		clearTimeout(wetWeightDebounceTimer)
	}
	wetWeightDebounceTimer = setTimeout(() => {
		calculateWetWeight()
	}, 1000) // Wait 1s after user stops typing
}

// Watch for changes in dry weight or concentration to trigger wet weight calculation
// Debounced to avoid excessive API calls while user is typing
watch([bulkMassOutput, concentrationOutput, selectedUnit, selectedConcentrationUnit], () => {
	debouncedCalculateWetWeight()
})

//check int process
let intermediateChecksResult = ref([] as IntermediateStatusStep[])
const expandedRowGroups = ref()

// let doseForms = [
// 	{ valueTxt: "doseform1", id: "1" },
// 	{ valueTxt: "doseform2", id: "2" },
// 	{ valueTxt: "doseform3", id: "3" },
// ]
// let doseUnits = [
// 	{ valueTxt: "doseunit1", id: "1" },
// 	{ valueTxt: "doseunit2", id: "2" },
// 	{ valueTxt: "doseunit3", id: "3" },
// ]
class tabActualStatus {
	actualMainTabId: string
	actualSubTabName: string
	selectedStepId: string
	generalInformationId: string
}

class allTabsActualStatus {
	public allTabsStatus: tabActualStatus[]
}
const showDialogWrongIds = ref(false)
const expandedRows = ref([])
const showMeWhereStepIdAreWrongInIntermediates = () => {
	apiClient.allIntermediatesStatusPerGeneralInformationId(props.assessmentId, props.tabId).then((res) => {
		intermediateChecksResult.value = res
		showDialogWrongIds.value = true
	})
}
const toggleRow = (row) => {
	const index = expandedRows.value.findIndex((expRow) => expRow.stepId === row.stepId)
	if (index === -1) {
		expandedRows.value.push(row)
	} else {
		expandedRows.value.splice(index, 1)
	}
}

function checkStepIdSelected() {
	if (
		props.tabName !== tabsDefinitions.packaging.value &&
		props.tabName !== tabsDefinitions.distribution.value &&
		props.tabName !== tabsDefinitions.consumerEndOfLife.value
	) {
		// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
		// if (tabStatus !== null) {
		// 	var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
		// 	if (toUpdate?.selectedStepId === undefined) {
		// 		nextPrevEnabled.value = checkIfAtLeastOneStepIsAvaliable()
		// 	} else {
		// 		nextPrevEnabled.value = true
		// 	}
		// }
		// object
		if (props.allTabsInformation.allTabsStatus === undefined) {
			nextPrevEnabled.value = false
			return
		}
		var tabData = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
		if (tabData?.selectedStepId === undefined) {
			nextPrevEnabled.value = checkIfAtLeastOneStepIsAvaliable()
		} else {
			nextPrevEnabled.value = true
		}
	}
}

const checkIfEnableTabsAfterGenInfo = () => {
	if (
		props.tabName === tabsDefinitions.syntheticAPI.value ||
		props.tabName === tabsDefinitions.biologics.value ||
		props.tabName === tabsDefinitions.packaging.value
	) {
		var tabData = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
		if (tabData?.selectedStepId === undefined) {
			return false
		} else {
			return true
		}
	}
	return true
}

function checkIfAtLeastOneStepIsAvaliable(): boolean {
	// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
	// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
	//var tabData = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
	if (generalInformationId.value !== null && generalInformationId.value !== undefined) {
		apiClient
			.getTheFirstStepByAssessmentIdScreenIdGeneralInformationId(props.assessmentId, props.tabId, generalInformationId.value)
			.then((response) => {
				if (response !== null) {
					nextPrevEnabled.value = true
					// toUpdate.selectedStepId = response.id
					// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))

					setTabInfoGlobally("selectedStepId", props.tabId, response.id)
					EventBus.emit("enableDisableRawMaterialTab_" + props.tabId, checkIfEnableTabsAfterGenInfo())
					EventBus.emit("selectedStep_" + props.tabId, response)
					EventBus.emit("highlightStep_" + props.tabId, response)
					return true
				} else {
					nextPrevEnabled.value = false
					return false
				}
			})
	}
	return false
}

// const allTabsInformation = reactive(props.allTabsInformation)

// watch(
// 	allTabsInformation,
// 	(newValue) => {

// 		// Emit an event to update allTabsInformation
// 		//emit("updateAllTabsInformation", allTabsInformation)
// 	},
// 	{ deep: true }
// )

// const thisTabInformations = ref()
const emit = defineEmits(["updateAllTabsInformation", "updateAllTabsInformationPerMainTab", "stepHovered"])
const setTabInfoGlobally = (type: string, tabId: string, tabContent: any) => {
	switch (type) {
		case "selectedStepId":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).selectedStepId = tabContent
			break
		case "generalInformationId":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).generalInformationId = tabContent
			break
	}
	emit("updateAllTabsInformation", props.allTabsInformation)
}

onMounted(() => {
	// Check feature flags first
	getUtilitiesWetBulkFeatureFlag()

	screenId = props.tabId
	generalInformation.screenId = props.tabId
	// generalInformation.assessmentId = props.assessmentId
	// setTimeout(() => {
	// }, 4000)
	//checkStepIdSelected()
	//getDropdowns()

	// Product Process checks are now handled by the tabName watcher
	// which runs immediately with { immediate: true }

	getDropdowns().then(() => {
		getGeneralInformation().then(() => {
			// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
			// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
			// toUpdate.generalInformationId = generalInformationId.value ? generalInformationId.value : ""
			// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))

			//setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)

			// if (
			// 	generalInformationId.value !== null &&
			// 	generalInformationId.value !== undefined &&
			// 	(props.tabName === tabsDefinitions.packaging.value || props.tabName === tabsDefinitions.distribution.value)
			// ) {
			// } else {
			// }
			checkStepIdSelected()

			// Update the active GeneralInformation count for Package/Distribution tabs
			updateActiveGeneralInformationCount()
		})
	})

	EventBus.on("checkStepIdSelected_" + props.tabId, () => {
		checkStepIdSelected()
	})

	EventBus.on("GeneralInformationUpdate" + props.tabId, () => {
		getGeneralInformation()
			.then(() => {
				// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
				// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
				// toUpdate.generalInformationId = generalInformationId.value ? generalInformationId.value : ""
				// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))

				setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)
				// if (
				// 	generalInformationId.value !== null &&
				// 	generalInformationId.value !== undefined &&
				// 	(props.tabName === tabsDefinitions.packaging.value || props.tabName === tabsDefinitions.distribution.value)
				// ) {
				// } else {
				// }
			})
			.then(() => {
				checkStepIdSelected()
				//setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)
			})
	})

	EventBus.on("addStepClicked", () => {
		newVersionNumber = 0
	})

	// Listen for reset form event from commonTabs (Package/Distribution Options)
	EventBus.on("resetGeneralInformationForm_" + props.tabId, () => {
		resetGeneralInformationForm()
	})

	// Listen for load specific GeneralInformation event from commonTabs (when selecting from list)
	EventBus.on("loadGeneralInformationById_" + props.tabId, (giId: unknown) => {
		if (giId && typeof giId === "string") {
			loadGeneralInformationById(giId)
		}
	})

	// Listen for multi-option feature flag status from commonTabs
	EventBus.on("multiOptionFeatureFlagLoaded_" + props.tabId, (isEnabled: unknown) => {
		isMultiOptionFeatureEnabled.value = isEnabled === true
	})

	EventBus.on("selectedAssessmentGeneralInformationTab_" + screenId, () => {
		checkStepIdSelected()
		if (generalInformationId.value !== null && generalInformationId.value !== undefined) {
			EventBus.emit("loadStepListBox_" + screenId, { screenId: screenId, generalInformationId: generalInformationId.value })
			if (props.tabName === tabsDefinitions.distribution.value) {
			}
		} else {
			//check generalInformationId again
			getGeneralInformation()
				.then(() => {
					// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
					// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
					// toUpdate.generalInformationId = generalInformationId.value ? generalInformationId.value : ""
					// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
					setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)
					EventBus.emit("enableDisableDeviceMaterialPackMaterialResults_" + props.tabId, true)
					EventBus.emit("showHideDeviceMaterialsSubTab_" + props.tabId, packDeviceUsed.value)
					EventBus.emit("setPackagingMaterialsAlwaysEnabled_" + props.tabId, alwaysEnablePackagingMaterials.value)
					if (
						(generalInformationId.value == null || generalInformationId.value == undefined) &&
						props.tabName !== tabsDefinitions.biologics.value &&
						props.tabName !== tabsDefinitions.productProcess.value &&
						props.tabName !== tabsDefinitions.packaging.value &&
						props.tabName !== tabsDefinitions.distribution.value
					) {
						saveGeneralInformation(false)
					} else {
						if (props.tabName === tabsDefinitions.distribution.value || props.tabName === tabsDefinitions.packaging.value) {
							// if (generalInformationId.value == null || generalInformationId.value == undefined) {
							EventBus.emit("enableDisableDeviceMaterialPackMaterialResults_" + props.tabId, false)
							// } else {
							// }
						}
					}
				})
				.then(() => {
					if (props.tabName === tabsDefinitions.distribution.value || props.tabName === tabsDefinitions.packaging.value) {
						if (generalInformationId.value == null || generalInformationId.value == undefined) {
							EventBus.emit("checkIfResultNeedToBeEnabled_" + props.tabId, props.assessmentId)
						}
					}
				})
		}
		setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)
	})
	EventBus.on("nextPrevEnabled_" + screenId, () => {
		//nextPrevEnabled.value = true
	})
	EventBus.on("selectedStep", (data: any) => {
		//nextPrevEnabled.value = true
	})
})

// Function to check if the "Utilities calculated using wet bulk substance" feature flag is enabled
async function getUtilitiesWetBulkFeatureFlag() {
	try {
		const featureFlag = await featureFlagClient.getByName("Utilities calculated using wet bulk substance")
		isUtilitiesWetBulkFeatureEnabled.value = featureFlag.isEnabled || false
	} catch (error) {
		// If the feature flag doesn't exist or there's an error, default to false
		isUtilitiesWetBulkFeatureEnabled.value = false
		console.warn("Feature flag 'Utilities calculated using wet bulk substance' not found or error occurred:", error)
	}
}

// Function to get the default value for "Calculate Results By" based on feature flag
// Only applies the default if no value is already saved in the database
async function getDefaultCalculateResultsByFeatureFlag() {
	// When locked by product process data, always use "Product Format"
	if (calculateResultsByLock.value.locked) {
		packCalculateResultsBy.value = "productFormat"
		return
	}

	// Check if a value is already saved in the database
	// packReferenceUnit is an object from masterDropdown, so check if it has an id
	// packSelectedProductFormatId is a string GUID
	const hasExistingReferenceUnit =
		packReferenceUnit.value != null &&
		packReferenceUnit.value !== "" &&
		(typeof packReferenceUnit.value === "object" ? packReferenceUnit.value.id != null : true)
	const hasExistingProductFormat = packSelectedProductFormatId.value != null && packSelectedProductFormatId.value !== ""

	if (hasExistingReferenceUnit) {
		// User already has Reference Unit selected - keep it
		packCalculateResultsBy.value = "referenceUnit"
		return
	}

	if (hasExistingProductFormat) {
		// User already has Product Format selected - keep it
		packCalculateResultsBy.value = "productFormat"
		return
	}

	// No existing selection - apply the feature flag default
	try {
		const featureFlag = await featureFlagClient.getByName("Default Device and Packaging Report By to 'Reference Unit'")
		// If feature flag is enabled, default to "referenceUnit" (as the flag name states)
		// If feature flag is disabled, default to "productFormat"
		packCalculateResultsBy.value = featureFlag.isEnabled ? "referenceUnit" : "productFormat"
	} catch (error) {
		// If the feature flag doesn't exist or there's an error, default to "referenceUnit"
		packCalculateResultsBy.value = "referenceUnit"
	}
}

watchEffect(() => {
	if (props.tabName !== null && props.tabName !== "" && getDone.value === false) {
		getDone.value = true
	}
})
function checkSingleField(field: any) {
	return field !== null && field !== "" && field !== undefined
}

function checkListField(field: any) {
	return field.length > 0
}

function checkMandatoryFields() {
	switch (props.tabName) {
		case tabsDefinitions.biologics.value:
			// Base mandatory fields
			let biologicsValid = checkSingleField(bulkMassOutput.value) && checkSingleField(selectedUnit.value) && checkSingleField(selectedSite.value)

			// Add concentration fields only if feature flag is enabled
			if (isUtilitiesWetBulkFeatureEnabled.value) {
				biologicsValid = biologicsValid && checkSingleField(concentrationOutput.value) && checkSingleField(selectedConcentrationUnit.value)
			}

			return biologicsValid
			break
		case tabsDefinitions.productProcess.value:
			return validateProductFormatSelection() && checkSingleField(massPerProductFormat.value) && checkSingleField(selectedMassUnit.value)
			break
		case tabsDefinitions.packaging.value:
			// Validation depends on Calculate Results By selection
			// Only validate generalInformationName if multi-option feature is enabled
			const packNameValid = isMultiOptionFeatureEnabled.value ? checkSingleField(generalInformationName.value) && !nameAlreadyExists.value : true

			// Common fields that apply regardless of Calculate Results By selection
			var commonFields = packNameValid

			// Additional validation based on Calculate Results By selection
			if (packCalculateResultsBy.value === "referenceUnit") {
				// When Reference Unit is selected, validate packReferenceUnit
				commonFields = commonFields && checkSingleField(packReferenceUnit.value)
			} else if (packCalculateResultsBy.value === "productFormat") {
				// When Product Format is selected, validate product format fields
				const productFormatValid = packProductFormatReadonly.value || checkSingleField(packSelectedProductFormatId.value)
				commonFields =
					commonFields && productFormatValid && checkSingleField(packMassPerProductFormat.value) && checkSingleField(packSelectedMassUnit.value)
			}

			if (packDeviceUsed.value) {
				// When device is used, only validate device-specific fields
				return (
					commonFields &&
					checkSingleField(packLifetimeRefUnitsPerDevice.value) &&
					checkSingleField(packDevicePerDevSecPack.value) &&
					checkSingleField(packDevSecPerDevTer.value) &&
					checkSingleField(packDevTerPerDevPallet.value)
				)
			} else {
				// When device is not used, validate standard packaging fields
				return (
					commonFields &&
					checkSingleField(packRefUnitPerPPack.value) &&
					checkSingleField(packPriPacksPerSecPack.value) &&
					checkSingleField(packSecPacksPerTerPack.value) &&
					checkSingleField(packTerPacksPerPallet.value)
				)
			}
			break
		case tabsDefinitions.distribution.value:
			// Only validate generalInformationName if multi-option feature is enabled
			const distNameValid = isMultiOptionFeatureEnabled.value ? checkSingleField(generalInformationName.value) && !nameAlreadyExists.value : true
			return (
				distNameValid &&
				checkSingleField(distDrugProductMass.value) &&
				checkSingleField(distDrugProductMassUnit.value) &&
				checkSingleField(distTotalMassOfPallet.value)
			)
			break
	}
	return true
}

function selectedCompOptionFilter(compOpSelected: any) {
	selectedCompOperations.value = unitOperations.value.filter((item: any) => item.compOptionId == compOpSelected)
	saveGeneralInformation(false)
}
function selectedCompOptionFilterOnDemand(compOpSelected: any) {
	selectedCompOperations.value = unitOperations.value.filter((item: any) => item.compOptionId == compOpSelected)
}
// function resetAllFields() {
// 	bulkMassOutput.value = null
// 	selectedUnit.value = null
// 	concentrationOutput.value = null
// 	selectedConcentrationUnit.value = null
// 	selectedSite.value = null
// }

// Opens the delete confirmation dialog for Package or Distribution
function openDeleteConfirmDialog(type: "packaging" | "distribution") {
	if (type === "packaging") {
		deleteDialogTitle.value = "Delete Packaging"
		deleteDialogItemType.value = "packaging"
	} else {
		deleteDialogTitle.value = "Delete Distribution"
		deleteDialogItemType.value = "distribution"
	}
	showDeleteConfirmDialog.value = true
}

// Confirms and executes the soft delete of the GeneralInformation
async function confirmDeleteGeneralInformation() {
	if (!generalInformationId.value) {
		showDeleteConfirmDialog.value = false
		return
	}

	try {
		await apiClient.softDeleteGeneralInformation(generalInformationId.value)

		toast.add({
			severity: "success",
			summary: `${deleteDialogItemType.value === "packaging" ? "Packaging" : "Distribution"} deleted successfully`,
			life: 3000,
			group: props.tabId,
		})

		// Refresh the general information options list in commonTabs.vue
		EventBus.emit("refreshGeneralInformationOptions_" + props.tabId)

		// Reload the general information to get the new selected one
		await getGeneralInformation()

		// Update the active count
		await updateActiveGeneralInformationCount()
	} catch (error: any) {
		console.error("Error deleting general information:", error)
		toast.add({
			severity: "error",
			summary: "Error deleting " + deleteDialogItemType.value,
			detail: error?.message || "An unexpected error occurred",
			life: 5000,
			group: props.tabId,
		})
	} finally {
		showDeleteConfirmDialog.value = false
	}
}

// Updates the count of active GeneralInformation records
async function updateActiveGeneralInformationCount() {
	if (props.tabName !== tabsDefinitions.packaging.value && props.tabName !== tabsDefinitions.distribution.value) {
		return
	}

	try {
		const count = await apiClient.getActiveGeneralInformationCount(props.assessmentId, props.tabId)
		activeGeneralInformationCount.value = count
	} catch (error) {
		console.error("Error getting active general information count:", error)
		activeGeneralInformationCount.value = 0
	}
}

function packDeviceUsedClick() {
	saveAttempted.value = false
	if (generalInformationId.value === null) {
		return
	}
	apiClient.checkIfDeviceIsUsedInPackagingMaterialsDeviceMaterials(generalInformationId.value).then((res: any) => {
		if (res.used && !packDeviceUsed.value) {
			var messages = []
			if (res.packUsed) messages.push("Packaging Materials")
			if (res.deviceUsed) messages.push("Device Materials")
			dialogDeviceUsedMessage.value =
				"Device material entries have been detected in the " +
				messages.join(" & ") +
				" tab" +
				(messages.length > 1 ? "s" : "") +
				". Please remove this entries manually before unselecting this option."
			showDialogDeviceUsed.value = true
			packDeviceUsed.value = true
		} else {
			if (packDeviceUsed.value) {
				packLifetimeRefUnitsPerDevice.value = null
				packDevicePerDevSecPack.value = null
				packDevSecPerDevTer.value = null
				packDevTerPerDevPallet.value = null
			}
		}
	})
}

function importMassFromProdProcess() {}

const fff = () => {}

// Simple approach: store and check the inherited value in the component
let inheritedProductFormatId: string | null = null

// Check for Product Process productFormatId that should be inherited in Packaging tab
// Uses direct database query via custom API endpoint
async function checkForProductProcessInheritance(): Promise<void> {
	if (props.tabName !== tabsDefinitions.packaging.value) return
	if (!props.assessmentId) return

	try {
		// Use the generated API client to get productFormatId from Product Process
		const genInfo = await apiClient.getProductFormatIdFromProductProcess(props.assessmentId)

		// Check if we got a valid GUID and if Packaging doesn't already have a value
		if (genInfo) {
			// Remove quotes if present (API may return quoted string)

			inheritedProductFormatId = genInfo.productFormatId!
			packSelectedProductFormatId.value = genInfo.productFormatId!
			packProductFormatReadonly.value = true
			packSelectedMassUnit.value = genInfo.massPerProductFormatUnitId!
			packMassPerProductFormat.value = genInfo.massPerProductFormat!

			// When product process has all three fields, lock Calculate Results By to "Product Format"
			if (calculateResultsByLockedByProductProcess.value) {
				packCalculateResultsBy.value = "productFormat"
			}

			// Set display value for "Product Format DevPack" field
			setTimeout(() => {
				if (useTreeSelect.value) {
					// Only set if ID exists in tree to avoid errors
					if (
						genInfo.productFormatId &&
						(productFormatTreeData.value.length === 0 || idExistsInTree(productFormatTreeData.value, genInfo.productFormatId))
					) {
						const selectionObj: any = {}
						selectionObj[genInfo.productFormatId!] = true
						packSelectedProductFormatIdDisplay.value = selectionObj
					} else {
						packSelectedProductFormatIdDisplay.value = {}
					}
				} else {
					packSelectedProductFormatIdDisplay.value = genInfo.productFormatId!
				}
			}, 500)
		} else {
		}
	} catch (error: any) {
		console.error("Error calling Product Process inheritance API:", error)

		// Fallback to the original method using tab information

		if (props.allTabsInformation && props.allTabsInformation.allTabsStatus) {
			const productProcessTab = props.allTabsInformation.allTabsStatus.find((tab: any) => tab.actualMainTabName === "Product Process")

			if (productProcessTab) {
				// Direct API call to get Product Process GeneralInformation (fallback)
				apiClient
					.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, productProcessTab.actualMainTabId)
					.then((productProcessGI: any[]) => {
						if (productProcessGI && productProcessGI.length > 0) {
							const productProcess = productProcessGI[0]

							if (productProcess.productFormatId && !packSelectedProductFormatId.value) {
								inheritedProductFormatId = productProcess.productFormatId
								packSelectedProductFormatId.value = productProcess.productFormatId
								packProductFormatReadonly.value = true

								setTimeout(() => {
									if (useTreeSelect.value) {
										// Only set if ID exists in tree to avoid errors
										if (
											productProcess.productFormatId &&
											(productFormatTreeData.value.length === 0 ||
												idExistsInTree(productFormatTreeData.value, productProcess.productFormatId as string))
										) {
											const selectionObj: any = {}
											selectionObj[productProcess.productFormatId as string] = true
											packSelectedProductFormatIdDisplay.value = selectionObj
										} else {
											packSelectedProductFormatIdDisplay.value = {}
										}
									} else {
										packSelectedProductFormatIdDisplay.value = productProcess.productFormatId
									}
								}, 500)
							}
						}
					})
					.catch((fallbackError: any) => {
						console.error("Error in fallback method:", fallbackError)
					})
			}
		}
	}
}

// Check if Product Process has a productFormatId that should be inherited
function checkProductProcessFormatId(): void {
	if (props.tabName !== tabsDefinitions.packaging.value) return

	// Simple implementation: this will be set when we find an existing productFormatId
	// during the normal loading process in getGeneralInformation

	if (inheritedProductFormatId) {
		packSelectedProductFormatId.value = inheritedProductFormatId
		packProductFormatReadonly.value = true

		setTimeout(() => {
			if (useTreeSelect.value) {
				// Only set if ID exists in tree to avoid errors
				if (
					inheritedProductFormatId &&
					(productFormatTreeData.value.length === 0 || idExistsInTree(productFormatTreeData.value, inheritedProductFormatId))
				) {
					const selectionObj: any = {}
					selectionObj[inheritedProductFormatId] = true
					packSelectedProductFormatIdDisplay.value = selectionObj
				} else {
					packSelectedProductFormatIdDisplay.value = {}
				}
			} else {
				packSelectedProductFormatIdDisplay.value = inheritedProductFormatId
			}
		}, 1000)
	}
}

function getSelectedProductFormatName(): string {
	if (!packSelectedProductFormatId.value) return ""

	// Try to find the name in productFormats (flat list)
	const formatFromList = productFormats.value.find((f) => f.id === packSelectedProductFormatId.value)
	if (formatFromList) return formatFromList.formatName || ""

	// Try to find in tree data (recursively search all nodes)
	function findInTree(nodes: any[], id: string): string | null {
		for (const node of nodes) {
			if (node.key === id) {
				return node.label || node.name || id
			}
			if (node.children && node.children.length > 0) {
				const found = findInTree(node.children, id)
				if (found) return found
			}
		}
		return null
	}

	const nameFromTree = findInTree(productFormatTreeData.value, packSelectedProductFormatId.value)
	return nameFromTree || packSelectedProductFormatId.value
}

function checksIfCompOpPack(selectedValue: any) {
	// For packaging tab, extract the key if it's an object (from TreeSelect)
	// or use the value directly if it's a string (from Dropdown)
	if (typeof selectedValue === "object" && selectedValue !== null) {
		packSelectedProductFormatId.value = selectedValue.key || selectedValue
	} else {
		packSelectedProductFormatId.value = selectedValue
	}
}

function checksIfCompOp(selectedValue: any) {
	// TreeSelect in PrimeVue returns the full node object when selection changes
	// We need to extract just the key (ID) for the API
	let actualValue = selectedValue

	// Check if it's a node object from TreeSelect
	if (selectedValue && typeof selectedValue === "object" && "key" in selectedValue) {
		actualValue = selectedValue.key
	}

	// Update the model with just the ID string
	selectedProductFormatId.value = actualValue

	if (!generalInformationId.value || generalInformationId.value === null || generalInformationId.value === undefined) {
		selectedCompOptionFilterOnDemand(actualValue)
		return
	}
	apiClient.getStepByAssessmentIdScreenIdGeneralInfomationId(props.assessmentId, props.tabId, generalInformationId.value).then(
		(res) => {
			if (res && res != null && res.length > 0) {
				toast.add({
					severity: "error",
					summary: `Action not allowed. Please remove any steps in the Step Details Section before changing the Product Format`,
					life: 4000,
					group: props.tabId,
				})
				// Reset to previous value
				if (productFormats.value && productFormats.value.length > 0) {
					selectedProductFormatId.value = oldProductFormatId
				} else {
					compOpSelected.value = oldProductFormatId
				}
			} else {
				// Store the old value before changing
				oldProductFormatId = selectedValue
				// Update compOpSelected for backward compatibility
				if (productFormats.value && productFormats.value.length > 0) {
					compOpSelected.value = selectedValue
				}
				selectedCompOptionFilter(compOpSelected.value || selectedValue)
			}
		},
		(err) => {
			toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
		}
	)
}

function onlyNumbers(evt: any) {
	evt = evt ? evt : window.event
	var charCode = evt.which ? evt.which : evt.keyCode
	//if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
	if (
		(charCode != 46 || evt.target.value.indexOf(".") != -1) && // “.” CHECK DOT, AND ONLY ONE.
		(charCode < 48 || charCode > 57)
	) {
		evt.preventDefault()
		return false
	} else {
		return true
	}
}

function loadProductFormatsFlatList(): Promise<void> {
	return new Promise((resolve) => {
		if (productFormatClient && typeof productFormatClient.list === "function") {
			productFormatClient
				.list()
				.then((productFormatsRes: ProductFormat[]) => {
					productFormats.value = productFormatsRes || []
					// Populate cache with flat list data
					productFormatsRes?.forEach((pf) => {
						const existing = productFormatCache.value.find((cached) => cached.id === pf.id)
						if (!existing) {
							productFormatCache.value.push(pf)
						}
					})
					useTreeSelect.value = false
					resolve()
				})
				.catch(() => {
					productFormats.value = []
					useTreeSelect.value = false
					resolve()
				})
		} else {
			productFormats.value = []
			useTreeSelect.value = false
			resolve()
		}
	})
}

/**
 * Validates the product format selection based on the current mode (tree or dropdown)
 * @returns {boolean} True if a valid selection is made
 */
function validateProductFormatSelection(): boolean {
	if (useTreeSelect.value) {
		// For tree selection, check both the display value and the actual ID
		// The display value might be an object or undefined when empty
		const hasDisplayValue =
			selectedProductFormatIdDisplay.value !== null &&
			selectedProductFormatIdDisplay.value !== undefined &&
			(typeof selectedProductFormatIdDisplay.value === "object" ? Object.keys(selectedProductFormatIdDisplay.value).length > 0 : false)
		const hasIdValue =
			selectedProductFormatId.value !== null &&
			selectedProductFormatId.value !== undefined &&
			selectedProductFormatId.value !== "" &&
			selectedProductFormatId.value !== "00000000-0000-0000-0000-000000000000"
		// Both display and ID must be valid
		return hasDisplayValue && hasIdValue
	} else {
		// For dropdown selection, validate normally
		return (
			selectedProductFormatId.value !== null &&
			selectedProductFormatId.value !== undefined &&
			selectedProductFormatId.value !== "" &&
			selectedProductFormatId.value !== "00000000-0000-0000-0000-000000000000"
		)
	}
}

/**
 * Expected tree data structure for TreeSelect:
 * [
 *   {
 *     key: "screen-id-1",
 *     label: "Screen Name API Based 1",
 *     data: "screen-data",
 *     selectable: false, // Screens should not be selectable
 *     children: [
 *       {
 *         key: "format-id-1",
 *         label: "Product Format Name 1",
 *         data: "format-data",
 *         selectable: true // Only product formats should be selectable
 *       },
 *       {
 *         key: "format-id-2",
 *         label: "Product Format Name 2",
 *         data: "format-data",
 *         selectable: true
 *       }
 *     ]
 *   }
 * ]
 */

function getDropdowns(): Promise<string> {
	return new Promise((resolve, reject) => {
		switch (props.tabName) {
			case tabsDefinitions.distribution.value:
				apiClient.getDistDropdowns().then((res) => {
					if (res.weightUnits) distReferenceUnits.value = res.weightUnits.filter((item: any) => item.unitName !== "ml" && item.unitName !== "l")
					resolve("Done")
				})
				break
			case tabsDefinitions.packaging.value:
				apiClient
					.getPackDropdowns()
					.then((res) => {
						packReferenceUnits.value = res.referenceUnits

						// Reset readonly flag initially
						packProductFormatReadonly.value = false

						// Check for Product Process inheritance first
						checkProductProcessFormatId()

						// Load mass units (g and mg only) for packaging
						return apiClient.getRawMaterialsRelatedDropdowns(props.tabId, props.assessmentId)
					})
					.then((rawMatRes) => {
						if (rawMatRes?.unitsWeight) {
							packMassUnits.value = rawMatRes.unitsWeight.filter((unit: any) => unit.unitName === "g" || unit.unitName === "mg")
						}

						// Load product formats for packaging tab
						if (productFormatClient && typeof productFormatClient.getTreeStructure === "function") {
							productFormatClient
								.getTreeStructure()
								.then((treeData) => {
									// Helper to extract product formats from tree and populate cache
									const extractProductFormats = (nodes: any[]): void => {
										nodes.forEach((node) => {
											if (node.children && node.children.length > 0) {
												// Process children for product formats
												node.children.forEach((child: any) => {
													if (child.data && child.data.type === "productFormat") {
														// Add to cache if not already present
														const existing = productFormatCache.value.find((pf) => pf.id === child.data.id)
														if (!existing) {
															productFormatCache.value.push({
																id: child.data.id,
																formatName: child.data.formatName,
																massQuantityLabelText: child.data.massQuantityLabelText,
																screenId: child.data.screenId,
															} as ProductFormat)
														}
													}
												})
												extractProductFormats(node.children)
											}
										})
									}

									// Extract and cache product formats before processing tree
									extractProductFormats(treeData || [])

									// Process the tree data to set selectable properties
									const processTreeData = (nodes: any[]): any[] => {
										return nodes
											.filter((node) => node && typeof node === "object") // Filter out invalid nodes
											.map((node) => {
												const processedNode = { ...node }

												// Ensure key exists - TreeSelect requires this property
												if (!processedNode.key) {
													console.warn("Node missing key property:", processedNode)
													return null // Skip invalid nodes
												}

												// If this node has children, it's a screen (parent) and should not be selectable
												if (processedNode.children && processedNode.children.length > 0) {
													processedNode.selectable = false
													// Set expanded to false initially to avoid aria-hidden conflicts
													processedNode.expanded = false
													// Ensure the node has proper aria attributes
													processedNode.leaf = false
													// Recursively process children
													processedNode.children = processTreeData(processedNode.children)
												} else {
													// Leaf nodes (product formats) should be selectable
													processedNode.selectable = true
													processedNode.leaf = true
												}

												return processedNode
											})
											.filter((node) => node !== null) // Remove any null entries from invalid nodes
									}

									productFormatTreeData.value = processTreeData(treeData || [])
									useTreeSelect.value = productFormatTreeData.value.length > 0
									resolve("Done")
								})
								.catch((error) => {
									console.warn("Failed to load tree structure for packaging, falling back to flat list:", error)
									// Fallback to flat list if tree structure fails
									loadProductFormatsFlatList().then(() => resolve("Done"))
								})
						} else {
							// Fallback to flat list if tree API not available
							loadProductFormatsFlatList().then(() => resolve("Done"))
						}
					})
				break
			case tabsDefinitions.productProcess.value:
				// Load dose dropdowns first
				apiClient
					.getdosesDropdowns()
					.then((dosesRes) => {
						compOperations.value = dosesRes.compOptions
						unitOperations.value = dosesRes.unitOperations
						// Load mass units (g and mg only) from the API
						return apiClient.getRawMaterialsRelatedDropdowns(props.tabId, props.assessmentId)
					})
					.then((rawMatRes) => {
						if (rawMatRes?.unitsWeight) {
							massUnits.value = rawMatRes.unitsWeight.filter((unit: any) => unit.unitName === "g" || unit.unitName === "mg")
						}

						// Try to load product formats tree structure if available
						if (productFormatClient && typeof productFormatClient.getTreeStructure === "function") {
							productFormatClient
								.getTreeStructure()
								.then((treeData) => {
									// Helper to extract product formats from tree and populate cache
									const extractProductFormats = (nodes: any[]): void => {
										nodes.forEach((node) => {
											if (node.children && node.children.length > 0) {
												// Process children for product formats
												node.children.forEach((child: any) => {
													if (child.data && child.data.type === "productFormat") {
														// Add to cache if not already present
														const existing = productFormatCache.value.find((pf) => pf.id === child.data.id)
														if (!existing) {
															productFormatCache.value.push({
																id: child.data.id,
																formatName: child.data.formatName,
																massQuantityLabelText: child.data.massQuantityLabelText,
																screenId: child.data.screenId,
															} as ProductFormat)
														}
													}
												})
												extractProductFormats(node.children)
											}
										})
									}

									// Extract and cache product formats before processing tree
									extractProductFormats(treeData || [])

									// Process the tree data to set selectable properties
									const processTreeData = (nodes: any[]): any[] => {
										return nodes
											.filter((node) => node && typeof node === "object") // Filter out invalid nodes
											.map((node) => {
												const processedNode = { ...node }

												// Ensure key exists - TreeSelect requires this property
												if (!processedNode.key) {
													console.warn("Node missing key property:", processedNode)
													return null // Skip invalid nodes
												}

												// If this node has children, it's a screen (parent) and should not be selectable
												if (processedNode.children && processedNode.children.length > 0) {
													processedNode.selectable = false
													// Set expanded to false initially to avoid aria-hidden conflicts
													processedNode.expanded = false
													// Ensure the node has proper aria attributes
													processedNode.leaf = false
													// Recursively process children
													processedNode.children = processTreeData(processedNode.children)
												} else {
													// Leaf nodes (product formats) should be selectable
													processedNode.selectable = true
													processedNode.leaf = true
												}

												return processedNode
											})
											.filter((node) => node !== null) // Remove any null entries from invalid nodes
									}

									productFormatTreeData.value = processTreeData(treeData || [])
									useTreeSelect.value = productFormatTreeData.value.length > 0

									// If we have tree data and an existing selection, ensure compatibility
									if (useTreeSelect.value && selectedProductFormatId.value) {
										// The existing selectedProductFormatId should work with tree structure
										// as it represents the product format ID that should match tree node keys
									}

									resolve("Done")
								})
								.catch((error) => {
									console.warn("Failed to load tree structure, falling back to flat list:", error)
									// Fallback to flat list if tree structure fails
									loadProductFormatsFlatList().then(() => resolve("Done"))
								})
						} else {
							// Fallback to flat list if tree API not available
							loadProductFormatsFlatList().then(() => resolve("Done"))
						}
					})
					.catch(() => {
						resolve("Done")
					})
				break
			case tabsDefinitions.biologics.value:
				apiClient.getBioGeneralInfoDropdowns().then((res) => {
					unitsWeight.value = res.weightUnits
					concentrationUnits.value = res.concentrationUnits
					siteLocations.value = res.locations
					resolve("Done")
				})
				break
			default:
				resolve("Done")
		}
	})
}

// function checkIfStepIsSelected() {
// 	var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
// 	var toGetStatus = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
// 	if (toGetStatus?.selectedStepId === undefined) return false
// 	return true
// }

function nextPage() {
	if (assessmentStore.isLocked) {
		// Skip saving, just go to next page
		EventBus.emit("nextPreviousTab_" + props.tabId, {
			action: "next",
			actualTab: props.tabId,
		})
	} else {
		// Save first, then go to next page
		saveGeneralInformation(true).then((res) => {
			if (res) {
				EventBus.emit("nextPreviousTab_" + props.tabId, {
					action: "next",
					actualTab: props.tabId,
				})
			}
		})
	}
}

let isGetGeneralInformationRunning = false
function getGeneralInformation(): Promise<string> {
	// Prevent duplicate calls if one is already running
	if (isGetGeneralInformationRunning) {
		return Promise.resolve("blocked")
	}

	isGetGeneralInformationRunning = true
	return new Promise((resolve, reject) => {
		if (props.assessmentId != null && props.tabId != null) {
			apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId).then((result) => {
				// Populate existing option names for duplicate name validation
				existingOptionNames.value = result
					.filter((gi: any) => gi.isActive !== false)
					.map((gi: any) => gi.generalInformationName || gi.versionName || "")
					.filter((name: string) => name !== "")
				if (result.length != 0) {
					switch (props.tabName) {
						case tabsDefinitions.distribution.value:
							// Find the selected option (wasSelected = true) or fall back to first option
							const distributionGI = result.find((item) => item.wasSelected === true) || result[0]
							//distDosesPerPallet.value = distributionGI.distDosesPerPallet
							distDrugProductMass.value = distributionGI.distDrugProductMass
							distTotalMassOfPallet.value = distributionGI.distTotalMassOfPallet
							distDrugProductMassUnit.value = distributionGI.distDrugProductMassUnit
							distTotalMassOfPalletUnit.value = distributionGI.distTotalMassOfPalletUnitId
							generalInformationName.value = distributionGI.generalInformationName || ""
							originalLoadedName.value = generalInformationName.value
							// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
							// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
							// toUpdate.selectedStepId = distributionGI.step[0].id ? distributionGI.step[0].id : ""
							// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
							if (distributionGI.step && distributionGI.step[0]) {
								setTabInfoGlobally("selectedStepId", props.tabId, distributionGI.step[0].id)
							}
							EventBus.emit("enableDisableRawMaterialTab_" + props.tabId, checkIfEnableTabsAfterGenInfo())
							nextPrevEnabled.value = true
							break
						case tabsDefinitions.productProcess.value:
							// Load product format ID if available
							const productFormatIdValue = result[0].productFormatId

							// Only set values if they exist
							if (productFormatIdValue) {
								selectedProductFormatId.value = productFormatIdValue
								oldProductFormatId = productFormatIdValue

								// Set display value immediately to avoid flashing
								if (useTreeSelect.value) {
									// Only set if ID exists in tree to avoid errors
									if (productFormatTreeData.value.length === 0 || idExistsInTree(productFormatTreeData.value, productFormatIdValue)) {
										selectedProductFormatIdDisplay.value = { [productFormatIdValue]: true }
									} else {
										selectedProductFormatIdDisplay.value = {}
									}
								} else {
									selectedProductFormatIdDisplay.value = productFormatIdValue
								}
							} else {
								// Clear values if no product format
								selectedProductFormatId.value = null
								selectedProductFormatIdDisplay.value = useTreeSelect.value ? {} : null
							}

							// Load other fields
							compOpSelected.value = result[0].compOptionId
							doseStrength.value = result[0].doseStrength
							applyDefaultLocation.value = result[0].isDefaultLoaction
							massPerProductFormat.value = result[0].massPerProductFormat
							selectedMassUnit.value = result[0].massPerProductFormatUnitId

							// Update comp operations after a delay
							// setTimeout(() => {
							// 	selectedCompOptionFilter(result[0].compOptionId)
							// }, 300)

							break
						case tabsDefinitions.biologics.value:
							bulkMassOutput.value = result[0].proteinMassOutput
							selectedUnit.value = result[0].proteinMOUnitId
							concentrationOutput.value = result[0].concProteinMassProdOutput
							concentrationWetOutput.value = result[0].wetWeightOutput
							selectedConcentrationUnit.value = result[0].concentrationUnitId
							selectedSite.value = result[0].siteLocationId
							break
						case tabsDefinitions.packaging.value:
							// Find the selected option (wasSelected = true) or fall back to first valid option
							let packagingGI: any =
								result.find((item) => item.wasSelected === true && !item.notes?.startsWith("consumerEndOfLife_")) ||
								result.find((item) => !item.notes?.startsWith("consumerEndOfLife_"))
							if (packagingGI !== undefined) {
								packDeviceUsed.value = packagingGI.packDeviceUsed ? packagingGI.packDeviceUsed : false
								// Load the product format ID from the correct productFormatId field
								packSelectedProductFormatId.value = packagingGI.productFormatId

								// Set the display value immediately so the Select shows the saved selection on reload.
								if (packagingGI.productFormatId) {
									if (useTreeSelect.value) {
										if (productFormatTreeData.value.length === 0 || idExistsInTree(productFormatTreeData.value, packagingGI.productFormatId)) {
											packSelectedProductFormatIdDisplay.value = { [packagingGI.productFormatId]: true }
										} else {
											packSelectedProductFormatIdDisplay.value = {}
										}
									} else {
										packSelectedProductFormatIdDisplay.value = packagingGI.productFormatId
									}
								} else {
									packSelectedProductFormatIdDisplay.value = {}
								}

								packReferenceUnit.value = packagingGI.packReferenceUnit
								packDoseStrength.value = packagingGI.packDoseStrength
								packRefUnitPerPPack.value = packagingGI.packRefUnitPerPPack
								packPriPacksPerSecPack.value = packagingGI.packPriPacksPerSecPack
								packSecPacksPerTerPack.value = packagingGI.packSecPacksPerTerPack
								packTerPacksPerPallet.value = packagingGI.packTerPacksPerPallet
								packLifetimeRefUnitsPerDevice.value = packagingGI.packLifetimeRefUnitsPerDevice
								packDevicePerDevSecPack.value = packagingGI.packDevicePerDevSecPack
								packDevSecPerDevTer.value = packagingGI.packDevSecPerDevTer
								packDevTerPerDevPallet.value = packagingGI.packDevTerPerDevPallet
								// Load Mass per Product Format fields
								packMassPerProductFormat.value = packagingGI.massPerProductFormat
								packSelectedMassUnit.value = packagingGI.massPerProductFormatUnitId
								// Load Package Name
								generalInformationName.value = packagingGI.generalInformationName || ""
								originalLoadedName.value = generalInformationName.value
								setTimeout(() => {
									EventBus.emit("showHideDeviceMaterialsSubTab_" + props.tabId, packagingGI.packDeviceUsed)
									EventBus.emit("setPackagingMaterialsAlwaysEnabled_" + props.tabId, alwaysEnablePackagingMaterials.value)
								}, 250)
								// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
								// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
								// toUpdate.selectedStepId = packagingGI.step[0].id ? packagingGI.step[0].id : ""
								// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
								setTabInfoGlobally("selectedStepId", props.tabId, packagingGI.step[0].id ? packagingGI.step[0].id : "")
								nextPrevEnabled.value = true
							}

							// Check for Product Process productFormatId inheritance
							checkForProductProcessInheritance()
							// Also update Product Process data state for validation
							checkProductProcessData()
							break
					}

					generalInformationList.value = result

					//if data exists then display general information based on version number ordering
					if (generalInformationList.value && generalInformationList.value.length > 0) {
						//create version list
						versionList.value = []
						generalInformationList.value.forEach((item) => {
							let versionObj = { versionNumber: item.versionNumber, versionName: item.versionName }
							versionList.value.push(versionObj)
						})

						displayVersionList.value = true

						// For Packaging/Distribution: use wasSelected item, not version-based selection
						if (props.tabName === tabsDefinitions.packaging.value) {
							generalInformation =
								result.find((item) => item.wasSelected === true && !item.notes?.startsWith("consumerEndOfLife_")) ||
								result.find((item) => !item.notes?.startsWith("consumerEndOfLife_"))
						} else if (props.tabName === tabsDefinitions.distribution.value) {
							generalInformation = result.find((item) => item.wasSelected === true) || result[0]
						} else {
							//get general information for first version (for other tabs)
							if (newVersionNumber != null && newVersionNumber > 0) {
								generalInformation = generalInformationList.value.find((a) => a.versionNumber == newVersionNumber)
							} else if (selectedVersion.value != null && selectedVersion.value > 0) {
								generalInformation = generalInformationList.value.find((a) => a.versionNumber == selectedVersion.value)
							} else {
								generalInformation = generalInformationList.value.find((a) => a.versionNumber == 1)
							}
						}

						if (generalInformation != null && generalInformation.versionNumber != null) {
							selectedVersion.value = generalInformation.versionNumber
						}

						if (generalInformation != null && generalInformation.id != null) {
							generalInformationId.value = generalInformation.id
						}

						if (
							(generalInformationId.value !== null || generalInformationId.value !== undefined) &&
							props.tabName === tabsDefinitions.packaging.value
						) {
						} else {
							if (props.tabName === tabsDefinitions.distribution.value || props.tabName === tabsDefinitions.packaging.value) {
							}
						}

						if (generalInformation != null && generalInformation.notes != null) {
							notes.value = generalInformation.notes
						}
					} else {
						displayVersionList.value = false
						generalInformationId.value = null
						generalInformation.screenId = props.tabId
						generalInformation.assessmentId = props.assessmentId

						// Check for Product Process inheritance when no packaging data exists
						if (props.tabName === tabsDefinitions.packaging.value) {
							checkForProductProcessInheritance()
							// Also update Product Process data state for validation
							checkProductProcessData()
						}
					}
				} else {
					// Check for Product Process inheritance when no packaging data exists
					if (props.tabName === tabsDefinitions.packaging.value) {
						checkForProductProcessInheritance()
						// Also update Product Process data state for validation
						checkProductProcessData()
					}
				}

				// Set default for Calculate Results By based on feature flag (only if no existing selection)
				// This runs after data is loaded (or when no data exists) so it can check for existing values
				if (props.tabName === tabsDefinitions.packaging.value) {
					getDefaultCalculateResultsByFeatureFlag()
				}

				isGetGeneralInformationRunning = false
				resolve("Done")
			})
		} else {
			isGetGeneralInformationRunning = false
		}
	})
}

// Load a specific GeneralInformation by ID (used when selecting from Package/Distribution Options list)
async function loadGeneralInformationById(giId: string): Promise<void> {
	if (!props.assessmentId || !props.tabId) return

	try {
		// Fetch all GeneralInformation for this assessment/screen
		const result = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)

		// Find the specific GeneralInformation by ID
		const selectedGI = result.find((gi) => gi.id === giId)
		if (!selectedGI) {
			console.error("GeneralInformation not found with ID:", giId)
			return
		}

		// Update the generalInformation object
		generalInformation = selectedGI
		generalInformationId.value = selectedGI.id ?? null
		notes.value = selectedGI.notes ?? ""
		generalInformationName.value = selectedGI.generalInformationName ?? ""
		originalLoadedName.value = generalInformationName.value

		// Update existing option names for duplicate validation
		existingOptionNames.value = result
			.filter((gi: any) => gi.isActive !== false)
			.map((gi: any) => gi.generalInformationName || gi.versionName || "")
			.filter((name: string) => name !== "")

		// Load tab-specific fields
		switch (props.tabName) {
			case tabsDefinitions.packaging.value:
				packDeviceUsed.value = selectedGI.packDeviceUsed ?? false
				packSelectedProductFormatId.value = selectedGI.productFormatId ?? null
				packReferenceUnit.value = selectedGI.packReferenceUnit ?? null
				packDoseStrength.value = selectedGI.packDoseStrength ?? ""
				packRefUnitPerPPack.value = selectedGI.packRefUnitPerPPack ?? null
				packPriPacksPerSecPack.value = selectedGI.packPriPacksPerSecPack ?? null
				packSecPacksPerTerPack.value = selectedGI.packSecPacksPerTerPack ?? null
				packTerPacksPerPallet.value = selectedGI.packTerPacksPerPallet ?? null
				packLifetimeRefUnitsPerDevice.value = selectedGI.packLifetimeRefUnitsPerDevice ?? null
				packDevicePerDevSecPack.value = selectedGI.packDevicePerDevSecPack ?? null
				packDevSecPerDevTer.value = selectedGI.packDevSecPerDevTer ?? null
				packDevTerPerDevPallet.value = selectedGI.packDevTerPerDevPallet ?? null
				packMassPerProductFormat.value = selectedGI.massPerProductFormat ?? null
				packSelectedMassUnit.value = selectedGI.massPerProductFormatUnitId ?? null

				// Set product format display value
				if (selectedGI.productFormatId) {
					if (useTreeSelect.value) {
						if (productFormatTreeData.value.length === 0 || idExistsInTree(productFormatTreeData.value, selectedGI.productFormatId)) {
							packSelectedProductFormatIdDisplay.value = { [selectedGI.productFormatId]: true }
						} else {
							packSelectedProductFormatIdDisplay.value = {}
						}
					} else {
						packSelectedProductFormatIdDisplay.value = selectedGI.productFormatId
					}
				} else {
					packSelectedProductFormatIdDisplay.value = useTreeSelect.value ? {} : null
				}

				// Check for Product Process inheritance
				checkForProductProcessInheritance()
				checkProductProcessData()

				// Recompute "Calculate Results By" based on the loaded package's data
				await getDefaultCalculateResultsByFeatureFlag()

				// Set step info and enable tabs
				if (selectedGI.step && selectedGI.step[0]) {
					setTabInfoGlobally("selectedStepId", props.tabId, selectedGI.step[0].id ?? "")
					EventBus.emit("selectedStep_" + props.tabId, selectedGI.step[0])
				}

				// Enable subtabs
				nextPrevEnabled.value = true
				setTimeout(() => {
					EventBus.emit("showHideDeviceMaterialsSubTab_" + props.tabId, packDeviceUsed.value)
					EventBus.emit("setPackagingMaterialsAlwaysEnabled_" + props.tabId, alwaysEnablePackagingMaterials.value)
					EventBus.emit("enableDisableDeviceMaterialPackMaterialResults_" + props.tabId, true)
					EventBus.emit("enableDisableRawMaterialTabForDeviceAndPackaging_" + props.tabId, true)
				}, 250)
				break

			case tabsDefinitions.distribution.value:
				distDrugProductMass.value = selectedGI.distDrugProductMass ?? null
				distTotalMassOfPallet.value = selectedGI.distTotalMassOfPallet ?? null
				distDrugProductMassUnit.value = selectedGI.distDrugProductMassUnit ?? null
				distTotalMassOfPalletUnit.value = selectedGI.distTotalMassOfPalletUnitId ?? null

				// Set step info and enable tabs
				if (selectedGI.step && selectedGI.step[0]) {
					setTabInfoGlobally("selectedStepId", props.tabId, selectedGI.step[0].id ?? "")
					EventBus.emit("selectedStep_" + props.tabId, selectedGI.step[0])
				}

				// Enable subtabs
				nextPrevEnabled.value = true
				EventBus.emit("enableDisableRawMaterialTab_" + props.tabId, checkIfEnableTabsAfterGenInfo())
				setTimeout(() => {
					EventBus.emit("enableDisableDeviceMaterialPackMaterialResults_" + props.tabId, true)
				}, 250)
				break

			case tabsDefinitions.biologics.value:
				bulkMassOutput.value = selectedGI.proteinMassOutput ?? null
				selectedUnit.value = selectedGI.proteinMOUnitId ?? null
				concentrationOutput.value = selectedGI.concProteinMassProdOutput ?? null
				concentrationWetOutput.value = selectedGI.wetWeightOutput ?? null
				selectedConcentrationUnit.value = selectedGI.concentrationUnitId ?? null
				selectedSite.value = selectedGI.siteLocationId ?? null
				break
		}

		// Update the list
		generalInformationList.value = result

		// Reset save attempt flag
		saveAttempted.value = false

		// Set global tab info
		setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)

		// Update the active count for delete button state
		updateActiveGeneralInformationCount()
	} catch (error) {
		console.error("Error loading GeneralInformation by ID:", error)
	}
}

function unitToKg(type: string) {
	switch (type) {
		case "kg":
			return 1
		case "g":
			return 1000
		case "mg":
			return 1000000
		case "ug":
			return 1000000000
	}
}

function drugMassToKgDistribution() {
	if (!distReferenceUnits.value) return null
	if (distDrugProductMass.value == null) return null
	if (!distDrugProductMassUnit.value) return null
	return distDrugProductMass.value / unitToKg(distReferenceUnits?.value.find((a) => a.id == distDrugProductMassUnit.value.id)?.unitName)
}

async function saveGeneralInformation(showToast: boolean): Promise<boolean> {
	saveAttempted.value = true

	if (!checkMandatoryFields()) {
		toast.add({
			severity: "error",
			summary: "Please fill in the highlighted fields as they are required",
			detail: ``,
			life: 3000,
			group: props.tabId,
		})

		return false
	}
	if (generalInformation != null && generalInformation.id == null) {
		// Don't generate UUID here - let API handle it
		// API will check if a record exists for this assessment/screen and reuse it
		//set version
		newVersionNumber = 1
		generalInformation.versionNumber = newVersionNumber
		generalInformation.versionName = "Version 1"
	} else if (generalInformation === undefined) {
		generalInformation = new GeneralInformation()
		// Don't generate UUID here - let API handle it
		// API will check if a record exists for this assessment/screen and reuse it
		//set version
		newVersionNumber = 1
		generalInformation.versionNumber = newVersionNumber
		generalInformation.versionName = "Version 1"
		generalInformation.screenId = props.tabId
		generalInformation.assessmentId = props.assessmentId
	}

	generalInformation.notes = notes.value

	generalInformation.doseFromId = undefined
	generalInformation.doseStrength = undefined
	generalInformation.doseUnitId = undefined
	generalInformation.isDefaultLoaction = undefined
	generalInformation.proteinMassOutput = undefined
	generalInformation.proteinMOUnitId = undefined
	generalInformation.concProteinMassProdOutput = undefined
	generalInformation.concentrationUnitId = undefined
	generalInformation.siteLocationId = undefined

	switch (props.tabName) {
		case tabsDefinitions.distribution.value:
			//generalInformation.distDosesPerPallet = distDosesPerPallet.value
			generalInformation.distDrugProductMass = distDrugProductMass.value
			generalInformation.distTotalMassOfPallet = distTotalMassOfPallet.value
			generalInformation.distDosesPerPalletUnitId = null
			generalInformation.distDrugProductMassUnitId = distDrugProductMassUnit.value.id
			// Set default name when multi-option feature is disabled, otherwise use user input
			generalInformation.generalInformationName = isMultiOptionFeatureEnabled.value ? generalInformationName.value : "Default Distribution"
			const unitName = distReferenceUnits.value.find((a: any) => a.id == distDrugProductMassUnit.value.id)
			if (unitName != null && unitName.unitName) {
				const unitToKgConverted = distDrugProductMass.value / unitToKg(unitName.unitName)
				if (unitToKgConverted > distTotalMassOfPallet.value) {
					toast.add({
						severity: "error",
						summary: "Error",
						detail: `Total mass of pallet value should be higher than Total drug product mass. Please review the input parameters`,
						life: 3000,
						group: props.tabId,
					})
					return false
				}
			}

			break
		case tabsDefinitions.packaging.value:
			generalInformation.packDeviceUsed = packDeviceUsed.value

			if (packCalculateResultsBy.value === "referenceUnit") {
				// When Reference Unit is selected, save only the Reference Unit
				generalInformation.packReferenceUnitId = packReferenceUnit.value?.id
				// Clear Product Format related fields
				generalInformation.productFormatId = undefined
				generalInformation.massPerProductFormat = undefined
				generalInformation.massPerProductFormatUnitId = undefined
			} else {
				// When Product Format is selected, save Product Format fields
				let packFormatId = packSelectedProductFormatId.value
				if (packFormatId && typeof packFormatId === "object" && "key" in packFormatId) {
					packFormatId = packFormatId.key
				}
				if (packFormatId) {
					generalInformation.productFormatId = packFormatId
				}
				generalInformation.massPerProductFormat = packMassPerProductFormat.value
				generalInformation.massPerProductFormatUnitId = packSelectedMassUnit.value
				// Clear Reference Unit field
				generalInformation.packReferenceUnitId = undefined
			}

			generalInformation.packDoseStrength = packDoseStrength.value
			generalInformation.packRefUnitPerPPack = packRefUnitPerPPack.value
			generalInformation.packPriPacksPerSecPack = packPriPacksPerSecPack.value
			generalInformation.packSecPacksPerTerPack = packSecPacksPerTerPack.value
			generalInformation.packTerPacksPerPallet = packTerPacksPerPallet.value
			generalInformation.packLifetimeRefUnitsPerDevice = packLifetimeRefUnitsPerDevice.value
			generalInformation.packDevicePerDevSecPack = packDevicePerDevSecPack.value
			generalInformation.packDevSecPerDevTer = packDevSecPerDevTer.value
			generalInformation.packDevTerPerDevPallet = packDevTerPerDevPallet.value
			// Set default name when multi-option feature is disabled, otherwise use user input
			generalInformation.generalInformationName = isMultiOptionFeatureEnabled.value ? generalInformationName.value : "Default Packaging"
			break
		case tabsDefinitions.productProcess.value:
			//checksIfCompOp(selectedProductFormatId.value)
			// Ensure we're sending a string ID, not an object
			let formatId = selectedProductFormatId.value
			if (formatId && typeof formatId === "object" && "key" in formatId) {
				formatId = formatId.key
			}
			generalInformation.productFormatId = formatId
			generalInformation.compOptionId = compOpSelected.value // Keep for backward compatibility
			generalInformation.doseStrength = doseStrength.value
			generalInformation.isDefaultLoaction = applyDefaultLocation.value
			generalInformation.massPerProductFormat = massPerProductFormat.value
			generalInformation.massPerProductFormatUnitId = selectedMassUnit.value
			// TODO: Add these fields to GeneralInformation model in API
			// generalInformation.massPerProductFormat = massPerProductFormat.value
			// generalInformation.massPerProductFormatUnitId = selectedMassUnit.value
			break
		case tabsDefinitions.biologics.value:
			generalInformation.proteinMassOutput = bulkMassOutput.value
			generalInformation.proteinMOUnitId = selectedUnit.value
			generalInformation.concProteinMassProdOutput = concentrationOutput.value
			generalInformation.wetWeightOutput = concentrationWetOutput.value
			generalInformation.concentrationUnitId = selectedConcentrationUnit.value
			generalInformation.siteLocationId = selectedSite.value
			break
	}
	generalInformation.assessmentId = props.assessmentId

	try {
		const res = await apiClient.postCommonAPIGeneralInformation(generalInformation)

		generalInformationId.value = res.id ?? null
		// Update the generalInformation object with the ID returned by the API
		// This ensures the object has the correct ID for subsequent saves
		if (generalInformation && res.id) {
			generalInformation.id = res.id
		}

		// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus

		// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)

		// toUpdate.generalInformationId = res.id ? res.id : ""
		// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
		//

		setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)

		//toast.add({ severity: "success", summary: "Saved!", detail: "", life: 3500 })

		switch (props.tabName) {
			case tabsDefinitions.packaging.value:
				// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
				// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
				// toUpdate.selectedStepId = res.step[0].id ? res.step[0].id : ""
				// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
				// setTabInfoGlobally("selectedStepId", props.tabId, res.step[0].id ? res.step[0].id : "")
				nextPrevEnabled.value = true
				if (res.step && res.step[0]) {
					EventBus.emit("selectedStep_" + props.tabId, res.step[0])
				}
				updateSubTabContent(props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId).actualSubTabName)
				EventBus.emit("enableDisableDeviceMaterialPackMaterialResults_" + props.tabId, true)
				EventBus.emit("showHideDeviceMaterialsSubTab_" + props.tabId, packDeviceUsed.value)
				EventBus.emit("setPackagingMaterialsAlwaysEnabled_" + props.tabId, alwaysEnablePackagingMaterials.value)
				EventBus.emit("enableDisableRawMaterialTabForDeviceAndPackaging_" + props.tabId, true)
				// Refresh the general information options list and select the saved item
				EventBus.emit("refreshGeneralInformationOptions_" + props.tabId, res.id)
				break
			case tabsDefinitions.distribution.value:
				// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
				// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
				// toUpdate.selectedStepId = res.step[0].id ? res.step[0].id : ""
				// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
				if (res.step && res.step[0]) {
					setTabInfoGlobally("selectedStepId", props.tabId, res.step[0].id ? res.step[0].id : "")
					EventBus.emit("selectedStep_" + props.tabId, res.step[0])
				}
				nextPrevEnabled.value = true
				updateSubTabContent(props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId).actualSubTabName)
				EventBus.emit("enableDisableDeviceMaterialPackMaterialResults_" + props.tabId, true)
				// Refresh the general information options list and select the saved item
				EventBus.emit("refreshGeneralInformationOptions_" + props.tabId, res.id)
				break
			case tabsDefinitions.productProcess.value:
				// selectedCompOptionFilterOnDemand("00000000-0000-0000-0000-000000000000")
				// setTimeout(() => {
				// 	selectedCompOptionFilterOnDemand(compOpSelected.value)
				// }, 250)

				// Automatically update Device & Packaging records with the same values
				if (generalInformation.productFormatId || generalInformation.massPerProductFormat || generalInformation.massPerProductFormatUnitId) {
					const updateRequest = new UpdateDevicePackagingRequest({
						assessmentId: props.assessmentId,
						productFormatId: generalInformation.productFormatId,
						massPerProductFormat: generalInformation.massPerProductFormat,
						massPerProductFormatUnitId: generalInformation.massPerProductFormatUnitId,
					})

					try {
						await apiClient.updateDevicePackagingFromProductProcess(updateRequest)
					} catch (error) {
						console.warn("Failed to update Device & Packaging records:", error)
						// Don't show error to user as this is automatic inheritance - the main save was successful
					}
				}

				break
			default:
				// Don't reload steps on save - it causes unnecessary graph flickering
				// Steps are already loaded and the save doesn't change them
				// EventBus.emit("loadStepListBox_" + screenId, { screenId: screenId, generalInformationId: generalInformation.id })
				break
		}

		// if (props.tabName === tabsDefinitions.packaging.value) {
		// 	var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus
		// 	var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
		// 	toUpdate.selectedStepId = res.step[0].id ? res.step[0].id : ""
		// 	localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
		// 	nextPrevEnabled.value = true
		// 	updateSubTabContent(toUpdate.actualSubTabName)
		// } else {
		// }
		if (showToast) toast.add({ severity: "success", summary: "Saved!", detail: "", life: 3500, group: props.tabId })
		EventBus.emit("deviceAndPackagingSaved")
		updateAssessmentUpdatedByDate(props.assessmentId)
		EventBus.emit("GeneralInformationUpdate" + props.tabId, {})
		EventBus.emit("saveGraphPositions_" + props.tabId)
		saveAttempted.value = false
		return true
	} catch (err) {
		toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
		return false
	}
}
function updateSubTabContent(tabName: String) {
	EventBus.emit(tabName + "Update" + props.tabId, {})
}
function addNewGeneralInformationVersion() {
	//get last version number
	if (generalInformation.versionNumber != null && generalInformation.versionNumber > 0) {
		newVersionNumber = generalInformation.versionNumber + 1
	}

	//set new verion means new generalInformationId
	displayVersionList.value = false
	notes.value = ""
	generalInformation = new GeneralInformation()
	generalInformation.screenId = props.tabId
	generalInformation.assessmentId = props.assessmentId
	generalInformation.id = self.crypto.randomUUID()
	generalInformationId.value = generalInformation.id
	generalInformation.versionName = ""
	generalInformation.versionNumber = newVersionNumber
	selectedVersion.value = newVersionNumber

	//update steps
	EventBus.emit("newGeneralInformation")
}

// Reset form to default values for creating a new Package/Distribution option
function resetGeneralInformationForm() {
	// Reset the generalInformation object
	generalInformation = new GeneralInformation()
	generalInformation.screenId = props.tabId
	generalInformation.assessmentId = props.assessmentId

	// Reset common fields
	generalInformationId.value = null
	notes.value = ""
	generalInformationName.value = ""
	originalLoadedName.value = ""

	// Reset Packaging fields
	packDeviceUsed.value = false
	packReferenceUnit.value = null
	packSelectedProductFormatId.value = null
	packSelectedProductFormatIdDisplay.value = {}
	packMassPerProductFormat.value = null
	packSelectedMassUnit.value = null
	packDoseStrength.value = ""
	packRefUnitPerPPack.value = null
	packPriPacksPerSecPack.value = null
	packSecPacksPerTerPack.value = null
	packTerPacksPerPallet.value = null
	packLifetimeRefUnitsPerDevice.value = null
	packDevicePerDevSecPack.value = null
	packDevSecPerDevTer.value = null
	packDevTerPerDevPallet.value = null
	packProductFormatReadonly.value = false

	// Reset Distribution fields
	distDrugProductMass.value = null
	distDrugProductMassUnit.value = null
	distTotalMassOfPallet.value = null
	distTotalMassOfPalletUnit.value = null

	// Reset version info
	displayVersionList.value = false
	selectedVersion.value = 0
	newVersionNumber = 0

	// Reset save attempt flag
	saveAttempted.value = false

	// For Packaging tab, apply feature flag default for Calculate Results By and check Product Process inheritance
	if (props.tabName === tabsDefinitions.packaging.value) {
		getDefaultCalculateResultsByFeatureFlag()
		checkForProductProcessInheritance()
	}

	// Emit event to reset steps and subtabs for this specific tab
	EventBus.emit("newGeneralInformation_" + props.tabId)
	// Also emit the global event for backwards compatibility
	EventBus.emit("newGeneralInformation")
}

function versionChange(versionNumber: number) {
	generalInformation = new GeneralInformation()

	generalInformation = generalInformationList.value.find((a) => a.versionNumber == versionNumber)

	generalInformationId.value = generalInformation.id
	notes.value = generalInformation.notes
	selectedVersion.value = versionNumber

	// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus

	// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)

	// toUpdate.generalInformationId = generalInformation.id ? generalInformation.id : ""
	// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
	setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)

	EventBus.emit("versionChange", generalInformationId.value)
	EventBus.emit("loadStepListBox_" + screenId, { screenId: screenId, generalInformationId: generalInformationId.value })
}

function testFields() {
	switch (props.tabName) {
		case tabsDefinitions.biologics.value:
			// Base mandatory fields
			let missingFields = []
			if (bulkMassOutput.value == null) missingFields.push("bulkMassOutput")
			if (selectedUnit.value == null) missingFields.push("selectedUnit")
			if (selectedSite.value == null) missingFields.push("selectedSite")

			// Add concentration fields only if feature flag is enabled
			if (isUtilitiesWetBulkFeatureEnabled.value) {
				if (concentrationOutput.value == null) missingFields.push("concentrationOutput")
				if (selectedConcentrationUnit.value == null) missingFields.push("selectedConcentrationUnit")
			}

			if (missingFields.length > 0) {
				toast.add({
					severity: "error",
					summary: "Error",
					detail: "You must complete the highlighted fields",
					life: 2500,
				})
				saveAttempted.value = true
				return false
			}
			break
		case "Synthetic API":
			break
		case tabsDefinitions.productProcess.value:
			// if (
			// 	doseForm.value == null ||
			// 	selectedUnit.value == null ||
			// 	doseStrength.value == null ||
			// 	doseUnit.value == null ||
			// 	applyDefaultLocation.value == null
			// ) {
			// 	toast.add({
			// 		severity: "error",
			// 		summary: "Error",
			// 		detail: "You must complete the highlighted fields",
			// 		life: 2500,
			// 	})
			// 	saveAttempted.value = true
			// 	return false
			// }
			break
	}

	return true
}

function cancelNewGeneralInformationVersion() {
	newVersionNumber = 0
	selectedVersion.value = newVersionNumber
	getGeneralInformation().then(() => {
		// var tabStatus: allTabsActualStatus = JSON.parse(localStorage.getItem("actualTabsStatus")) as allTabsActualStatus

		// var toUpdate = tabStatus.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)

		// toUpdate.generalInformationId = generalInformationId.value ? generalInformationId.value : ""
		// localStorage.setItem("actualTabsStatus", JSON.stringify(tabStatus))
		setTabInfoGlobally("generalInformationId", props.tabId, generalInformationId.value)

		versionChange(selectedVersion.value)
	})
}
</script>
<style scoped>
.rowContainer {
	display: flex;
	flex-direction: row;
}

.singleItem {
	margin-top: auto;
}
.p-confirm-dialog .p-dialog-footer .p-button:nth-child(2) {
	display: none;
}
.p-invalid {
	border-color: red !important;
}
.btn-outline {
	background-color: white; /* Sfondo bianco */
	color: #007bff; /* Colore del testo blu */
	border: 2px solid #007bff; /* Contorno blu */
	padding: 5px 10px; /* Padding per aumentare lo spazio intorno al testo */
	font-size: 12px; /* Dimensione del testo */
	border-radius: 3px; /* Angoli arrotondati */
	cursor: pointer; /* Cambia il cursore in una manina per indicare che è cliccabile */
	transition: all 0.3s ease; /* Effetto di transizione al passaggio del mouse */
}
.btn-disabled {
	background-color: grey;
	cursor: not-allowed;
	opacity: 0.6;
}
.btn-outline:hover {
	background-color: #007bff; /* Cambia lo sfondo in blu al passaggio del mouse */
	color: white; /* Cambia il colore del testo in bianco al passaggio del mouse */
}
</style>
